import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Badge,
    Box,
    Button,
    Chip,
    Collapse,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Popover,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Switch,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import AutoGraphOutlinedIcon from '@mui/icons-material/AutoGraphOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import _ from 'lodash';
import ReactGridLayout, { WidthProvider } from 'react-grid-layout';
import ReactECharts from 'echarts-for-react';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { alpha, useTheme } from '@mui/material/styles';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import dashboardServices from 'app/services/automations/dashboard-services';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAssistantPage } from 'app/shared/ai/AssistantLayoutContext';
import { GOOGLE_MAPS_API_KEY } from 'app/utils/constants/paths';
import { hasAnyProfile } from 'app/utils/appHelpers';
import { HEADER_HEIGHT } from 'app/layouts/vertical-default/constants';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const GridLayout = WidthProvider(ReactGridLayout);

const EDITOR_ROLES = ['supervisor', 'customer_admin', 'superadmin'];
const DEFAULT_OPERATORS = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'contains'];
const DEFAULT_AGGREGATIONS = ['avg', 'sum', 'min', 'max', 'count', 'last', 'increase'];
// Solo tienen sentido sobre valores acumulados (campos con counter: true, ej. energía en kWh).
const DEFAULT_COUNTER_AGGREGATIONS = ['increase'];
const AGGREGATION_LABELS = {
    avg: 'Promedio',
    sum: 'Suma',
    min: 'Mínimo',
    max: 'Máximo',
    count: 'Cantidad',
    last: 'Último',
    increase: 'Incremento',
};
const DATE_RANGE_CUSTOM_KEY = '__custom__';

const QUICK_RANGE_OPTIONS = [
    { key: 'last_15m', label: 'Últimos 15 minutos', durationMs: 15 * 60 * 1000 },
    { key: 'last_30m', label: 'Últimos 30 minutos', durationMs: 30 * 60 * 1000 },
    { key: 'last_1h', label: 'Última 1 hora', durationMs: 60 * 60 * 1000 },
    { key: 'last_3h', label: 'Últimas 3 horas', durationMs: 3 * 60 * 60 * 1000 },
    { key: 'last_6h', label: 'Últimas 6 horas', durationMs: 6 * 60 * 60 * 1000 },
    { key: 'last_12h', label: 'Últimas 12 horas', durationMs: 12 * 60 * 60 * 1000 },
    { key: 'last_24h', label: 'Últimas 24 horas', durationMs: 24 * 60 * 60 * 1000 },
    { key: 'last_2d', label: 'Últimos 2 días', durationMs: 2 * 24 * 60 * 60 * 1000 },
    { key: 'last_7d', label: 'Últimos 7 días', durationMs: 7 * 24 * 60 * 60 * 1000 },
    { key: 'last_14d', label: 'Últimos 14 días', durationMs: 14 * 24 * 60 * 60 * 1000 },
    { key: 'last_30d', label: 'Últimos 30 días', durationMs: 30 * 24 * 60 * 60 * 1000 },
    { key: 'last_90d', label: 'Últimos 90 días', durationMs: 90 * 24 * 60 * 60 * 1000 },
    { key: 'last_6mo', label: 'Últimos 6 meses', durationMs: 180 * 24 * 60 * 60 * 1000 },
    { key: 'last_1y', label: 'Último 1 año', durationMs: 365 * 24 * 60 * 60 * 1000 },
];
const QUICK_RANGE_BY_KEY = _.keyBy(QUICK_RANGE_OPTIONS, 'key');
const CUSTOM_RANGE_COMPACT_SHORTCUTS = [
    { key: 'today', label: 'Hoy', days: 1 },
    { key: 'yesterday', label: 'Ayer', days: 1, previousDay: true },
    { key: '7d', label: '7 días', days: 7 },
    { key: '30d', label: '30 días', days: 30 },
];
const DEFAULT_RELATIVE_PRESET = 'last_24h';
const DEFAULT_AUTO_REFRESH_INTERVAL = '1m';
const AUTO_REFRESH_INTERVAL_OPTIONS = [
    { value: 'off', label: 'Off' },
    { value: '5s', label: '5s' },
    { value: '10s', label: '10s' },
    { value: '30s', label: '30s' },
    { value: '1m', label: '1m' },
    { value: '5m', label: '5m' },
    { value: '15m', label: '15m' },
    { value: '30m', label: '30m' },
    { value: '1h', label: '1h' },
    { value: '2h', label: '2h' },
    { value: '1d', label: '1d' },
];
const AUTO_REFRESH_INTERVAL_MS = {
    off: 0,
    '5s': 5 * 1000,
    '10s': 10 * 1000,
    '30s': 30 * 1000,
    '1m': 60 * 1000,
    '5m': 5 * 60 * 1000,
    '15m': 15 * 60 * 1000,
    '30m': 30 * 60 * 1000,
    '1h': 60 * 60 * 1000,
    '2h': 2 * 60 * 60 * 1000,
    '1d': 24 * 60 * 60 * 1000,
};
const DEFAULT_METRIC_DECIMALS = 1;
const MAX_METRIC_DECIMALS = 6;
const FRIENDLY_FIELD_LABELS = {
    count_present: 'Ocupación',
    count_in: 'Entradas',
    count_out: 'Salidas',
    temperature: 'Temperatura (ºC)',
    device_temperature: 'Temperatura del dispositivo (ºC)',
    ext_sensor_temperature: 'Temperatura sensor externo (ºC)',
    humidity: 'Humedad (%)',
    energy: 'Energía (kWh)',
    battery_status: 'Batería (%)',
    device_name: 'Nombre de dispositivo',
    device_code: 'Código de dispositivo',
    store_name: 'Local',
};

const LEGACY_WIDGET_TYPE_MAP = {
    timeseries_oven: 'timeseries',
    energy: 'bar',
};

const EDITOR_WIDGET_TYPES = ['timeseries', 'bar', 'state_timeline', 'stat', 'table', 'map'];

const WIDGET_LABELS = {
    timeseries: 'Serie temporal',
    bar: 'Gráfico de barras',
    state_timeline: 'Gráfico de estado',
    stat: 'KPI',
    table: 'Tabla',
    map: 'Mapa',
};

const CHART_SERIES_COLORS = ['#5C149B', '#2563EB', '#0EA5E9', '#16A34A', '#F97316', '#DC2626', '#A855F7'];

const WIDGET_VISUAL_BY_TYPE = {
    timeseries: {
        accent: '#5C149B',
        tint: alpha('#5C149B', 0.12),
        Icon: TimelineOutlinedIcon,
    },
    bar: {
        accent: '#2563EB',
        tint: alpha('#2563EB', 0.12),
        Icon: BarChartOutlinedIcon,
    },
    state_timeline: {
        accent: '#0EA5E9',
        tint: alpha('#0EA5E9', 0.12),
        Icon: AutoGraphOutlinedIcon,
    },
    stat: {
        accent: '#7C3AED',
        tint: alpha('#7C3AED', 0.12),
        Icon: QueryStatsOutlinedIcon,
    },
    table: {
        accent: '#334155',
        tint: alpha('#334155', 0.12),
        Icon: TableChartOutlinedIcon,
    },
    map: {
        accent: '#0F766E',
        tint: alpha('#0F766E', 0.12),
        Icon: MapOutlinedIcon,
    },
};

const DEFAULT_WIDGET_VISUAL = {
    accent: '#5C149B',
    tint: alpha('#5C149B', 0.12),
    Icon: TimelineOutlinedIcon,
};

const OPERATOR_LABELS = {
    eq: '=',
    neq: '!=',
    gt: '>',
    gte: '>=',
    lt: '<',
    lte: '<=',
    in: 'IN',
    contains: 'Contiene',
};

const DEFAULT_LAYOUT = {
    x: 0,
    y: 0,
    w: 12,
    h: 9,
    minW: 3,
    minH: 5,
};

const chartWidgets = new Set(['timeseries', 'bar', 'state_timeline']);
const DASHBOARD_FILTER_KEYS = ['store_id', 'zone', 'kind', 'device_id'];

const WIDGET_FORM_RULES = {
    timeseries: { x: true, y: true, aggregation: true, bucket: false, groupBy: true, labelField: true, dateRange: false },
    bar: { x: true, y: true, aggregation: true, bucket: false, groupBy: true, labelField: true, dateRange: false },
    state_timeline: { x: true, y: true, aggregation: false, bucket: false, groupBy: true, labelField: true, dateRange: false },
    stat: { x: false, y: true, aggregation: true, bucket: false, groupBy: false, labelField: false, dateRange: false },
    table: { x: false, y: true, aggregation: true, bucket: false, groupBy: true, labelField: true, dateRange: false },
    map: { x: false, y: false, aggregation: false, bucket: false, groupBy: false, labelField: true, dateRange: false },
};

const canonicalWidgetType = (widgetType) => {
    const value = `${widgetType || ''}`.trim();
    if (!value) return 'timeseries';
    return LEGACY_WIDGET_TYPE_MAP[value] || value;
};

const widgetFormRulesFor = (widgetType) => {
    return WIDGET_FORM_RULES[canonicalWidgetType(widgetType)] || WIDGET_FORM_RULES.timeseries;
};

const getWidgetVisualMeta = (widgetType) => {
    return WIDGET_VISUAL_BY_TYPE[canonicalWidgetType(widgetType)] || DEFAULT_WIDGET_VISUAL;
};

const findFirstMetricField = (datasetsMap, datasetKey) => {
    const fields = datasetsMap?.[datasetKey]?.fields || [];
    return fields.find((field) => field.metric)?.key || '';
};

const prettifyFieldKey = (fieldKey) => {
    const key = `${fieldKey || ''}`.trim();
    if (!key) return '';
    return _.startCase(key.replace(/_/g, ' '));
};

const getFieldDisplayLabel = (fieldOrKey) => {
    if (_.isObject(fieldOrKey) && `${fieldOrKey?.label || ''}`.trim()) {
        return `${fieldOrKey.label}`.trim();
    }

    const key = _.isString(fieldOrKey)
        ? `${fieldOrKey}`.trim()
        : `${fieldOrKey?.key || ''}`.trim();
    if (!key) return '';

    return FRIENDLY_FIELD_LABELS[key] || prettifyFieldKey(key);
};

const getMetricLabelFromDatasetFields = (datasetFields, fieldKey) => {
    const key = `${fieldKey || ''}`.trim();
    if (!key) return '';
    const found = (datasetFields || []).find((field) => field.key === key);
    return getFieldDisplayLabel(found || key);
};

const extractMetricEntries = (rawMetrics) => {
    if (_.isNil(rawMetrics)) return [];
    if (Array.isArray(rawMetrics)) return rawMetrics;

    if (_.isString(rawMetrics) || _.isNumber(rawMetrics)) {
        const field = `${rawMetrics || ''}`.trim();
        return field ? [field] : [];
    }

    if (_.isObject(rawMetrics)) {
        const directField = `${rawMetrics?.field || ''}`.trim();
        if (directField) return [rawMetrics];

        const sortedKeys = Object.keys(rawMetrics).sort((a, b) => {
            const aNumber = Number(a);
            const bNumber = Number(b);
            const bothNumeric = Number.isFinite(aNumber) && Number.isFinite(bNumber);
            if (bothNumeric) return aNumber - bNumber;
            return `${a}`.localeCompare(`${b}`);
        });

        return sortedKeys.map((key) => rawMetrics[key]).filter((entry) => !_.isNil(entry));
    }

    return [];
};

const resolveRawQueryMetrics = (queryConfig = {}) => {
    const fromYMetrics = extractMetricEntries(queryConfig?.y_metrics);
    if (fromYMetrics.length) return fromYMetrics;
    return extractMetricEntries(queryConfig?.y);
};

const normalizeMetricEntriesForDataset = (rawMetrics, datasetFields = []) => {
    return extractMetricEntries(rawMetrics).map((metric) => {
        if (_.isString(metric)) {
            const field = `${metric}`.trim();
            if (!field) return null;
            return { field, label: getMetricLabelFromDatasetFields(datasetFields, field) };
        }

        const field = `${metric?.field || ''}`.trim();
        if (!field) return null;

        const rawLabel = `${metric?.label || ''}`.trim();
        const defaultLabel = getMetricLabelFromDatasetFields(datasetFields, field);
        return {
            ...metric,
            field,
            label: (!rawLabel || rawLabel === field) ? defaultLabel : rawLabel,
        };
    }).filter(Boolean);
};

const findDatasetDefaultX = (datasetsMap, datasetKey) => {
    const fields = datasetsMap?.[datasetKey]?.fields || [];
    return datasetsMap?.[datasetKey]?.default_x || fields.find((field) => field.x)?.key || '';
};

const findDatasetDefaultLabelField = (datasetsMap, datasetKey) => {
    const fields = datasetsMap?.[datasetKey]?.fields || [];
    const preferred = ['device_name', 'device_code', 'store_name', 'zone', 'kind'];

    for (const key of preferred) {
        if (fields.some((field) => field.key === key)) return key;
    }

    return fields.find((field) => field.dimension || field.groupable || field.x)?.key || '';
};

const findDatasetForMap = (datasetsMap) => {
    const preferred = ['latest_device_metrics', 'devices', 'device_metrics'];
    for (const key of preferred) {
        const fields = datasetsMap?.[key]?.fields || [];
        const hasCoordinates = fields.some((field) => field.key === 'latitude') && fields.some((field) => field.key === 'longitude');
        if (hasCoordinates) return key;
    }

    return Object.keys(datasetsMap || {})[0] || '';
};

const buildDefaultDateRangeState = () => ({
    mode: 'relative',
    preset: DEFAULT_RELATIVE_PRESET,
    from: '',
    to: '',
});

const normalizeAutoRefreshInterval = (value, fallback = DEFAULT_AUTO_REFRESH_INTERVAL) => {
    const normalized = `${value || ''}`.trim().toLowerCase();
    if (Object.prototype.hasOwnProperty.call(AUTO_REFRESH_INTERVAL_MS, normalized)) return normalized;

    const fallbackNormalized = `${fallback || ''}`.trim().toLowerCase();
    if (Object.prototype.hasOwnProperty.call(AUTO_REFRESH_INTERVAL_MS, fallbackNormalized)) return fallbackNormalized;

    return DEFAULT_AUTO_REFRESH_INTERVAL;
};

const normalizeAutoRefreshOptions = (options) => {
    const records = Array.isArray(options) ? options : [];
    const normalized = records
        .map((option) => {
            const rawValue = `${option?.value ?? option?.id ?? ''}`.trim().toLowerCase();
            if (!Object.prototype.hasOwnProperty.call(AUTO_REFRESH_INTERVAL_MS, rawValue)) return null;

            return {
                value: rawValue,
                label: `${option?.label || option?.name || (rawValue === 'off' ? 'Off' : rawValue)}`,
            };
        })
        .filter(Boolean);

    const deduped = _.uniqBy(normalized, 'value');
    return deduped.length ? deduped : AUTO_REFRESH_INTERVAL_OPTIONS;
};

const normalizeDateRangeState = (value) => {
    const hash = _.isObject(value) ? value : {};
    const mode = hash.mode === 'custom' ? 'custom' : 'relative';

    if (mode === 'custom') {
        return {
            mode: 'custom',
            preset: '',
            from: toDateTimeInput(hash.from),
            to: toDateTimeInput(hash.to),
        };
    }

    const preset = QUICK_RANGE_BY_KEY[hash.preset] ? hash.preset : DEFAULT_RELATIVE_PRESET;
    return {
        mode: 'relative',
        preset,
        from: '',
        to: '',
    };
};

const extractDateRangeState = (rawDateRange) => {
    const value = _.isObject(rawDateRange) ? rawDateRange : {};
    const mode = `${value.mode || ''}`.trim();

    if (mode === 'relative' || mode === 'custom') {
        return normalizeDateRangeState(value);
    }

    if (value.from || value.to || value.start || value.end) {
        return normalizeDateRangeState({
            mode: 'custom',
            from: value.from || value.start,
            to: value.to || value.end,
        });
    }

    return buildDefaultDateRangeState();
};

const buildDateRangePayload = (dateRangeState) => {
    const state = normalizeDateRangeState(dateRangeState);
    if (state.mode === 'relative') {
        return {
            mode: 'relative',
            preset: state.preset || DEFAULT_RELATIVE_PRESET,
        };
    }

    return {
        mode: 'custom',
        from: toISOStringOrNull(state.from),
        to: toISOStringOrNull(state.to),
    };
};

const resolveDateRangeAbsolute = (dateRangeState) => {
    const state = normalizeDateRangeState(dateRangeState);
    const now = new Date();

    if (state.mode === 'relative') {
        const presetDef = QUICK_RANGE_BY_KEY[state.preset || DEFAULT_RELATIVE_PRESET];
        if (!presetDef) return { from: null, to: null };
        const to = now;
        const from = new Date(now.getTime() - presetDef.durationMs);
        return { from, to };
    }

    const from = state.from ? new Date(state.from) : null;
    const to = state.to ? new Date(state.to) : null;
    return {
        from: from && !Number.isNaN(from.getTime()) ? from : null,
        to: to && !Number.isNaN(to.getTime()) ? to : null,
    };
};

const computeAutoBucket = (dateRangeState, widgetType) => {
    const effectiveType = canonicalWidgetType(widgetType);
    if (!['timeseries', 'bar', 'table', 'stat'].includes(effectiveType)) return 'raw';

    const absolute = resolveDateRangeAbsolute(dateRangeState);
    if (!absolute.from || !absolute.to) return 'raw';

    const durationMs = absolute.to.getTime() - absolute.from.getTime();
    if (durationMs <= 6 * 60 * 60 * 1000) return 'minute';
    if (durationMs <= 30 * 24 * 60 * 60 * 1000) return 'hour';
    if (durationMs <= 180 * 24 * 60 * 60 * 1000) return 'day';
    if (durationMs <= 540 * 24 * 60 * 60 * 1000) return 'week';
    return 'month';
};

const getEffectiveWidgetDateRange = (widgetForm, dashboardDateRangeState) => {
    if (!widgetForm) return normalizeDateRangeState(dashboardDateRangeState);

    const source = widgetForm.query_config?.date_range_source || 'dashboard';
    if (source === 'fixed') {
        return extractDateRangeState(widgetForm.query_config?.date_range || {});
    }

    return normalizeDateRangeState(dashboardDateRangeState);
};

const parseControlLimits = (raw = {}) => {
    const enabled = raw.enabled === true || `${raw.enabled || ''}`.toLowerCase() === 'true';
    const mode = raw.mode === 'outside' ? 'outside' : 'inside';
    const min = raw.min === '' || raw.min === null || raw.min === undefined ? null : Number(raw.min);
    const max = raw.max === '' || raw.max === null || raw.max === undefined ? null : Number(raw.max);

    return {
        enabled,
        mode,
        min: Number.isFinite(min) ? min : null,
        max: Number.isFinite(max) ? max : null,
    };
};

const parseAxisLimit = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const TABLE_COLUMN_TYPES = ['text', 'number', 'datetime'];
const TABLE_DATETIME_FORMATS = ['compact', 'friendly', 'iso'];
const TABLE_DATETIME_FORMAT_LABELS = {
    compact: 'Compacto',
    friendly: 'Amigable',
    iso: 'ISO',
};
// El ejemplo que se muestra al editar sale del mismo formateador que la tabla, para que no puedan separarse.
const TABLE_DATETIME_FORMAT_SAMPLE = new Date(2026, 4, 18, 19, 20);

const inferTableColumnType = (key = '') => {
    const normalized = `${key || ''}`.trim().toLowerCase();
    if (!normalized) return 'text';
    if (normalized.includes('time') || normalized.includes('date')) return 'datetime';
    if (normalized.startsWith('metric_') || normalized.includes('temp') || normalized.includes('value')) return 'number';
    return 'text';
};

const buildDefaultTableViewConfig = (yMetrics = []) => {
    const firstMetricLabel = `${yMetrics?.[0]?.label || ''}`.trim() || 'Valor';
    return {
        datetime_format: 'compact',
        columns: [
            { key: 'bucket_time', label: 'Fecha/Hora', type: 'datetime' },
            { key: 'group_0', label: 'Dispositivo', type: 'text' },
            { key: 'metric_0', label: firstMetricLabel, type: 'number' },
        ],
    };
};

const normalizeTableViewConfig = (rawConfig = {}, yMetrics = []) => {
    const defaults = buildDefaultTableViewConfig(yMetrics);
    const config = _.isObject(rawConfig) ? rawConfig : {};
    const rawColumns = Array.isArray(config.columns) ? config.columns : defaults.columns;

    const normalizedColumns = rawColumns
        .map((column, index) => {
            const key = `${column?.key || ''}`.trim();
            if (!key) return null;
            const label = `${column?.label || ''}`.trim() || _.startCase(key.replace(/_/g, ' '));
            const type = TABLE_COLUMN_TYPES.includes(column?.type) ? column.type : inferTableColumnType(key);
            return {
                id: `${column?.id || `col_${index}_${key}`}`,
                key,
                label,
                type,
            };
        })
        .filter(Boolean);

    return {
        datetime_format: TABLE_DATETIME_FORMATS.includes(config.datetime_format) ? config.datetime_format : defaults.datetime_format,
        columns: normalizedColumns.length ? normalizedColumns : defaults.columns.map((column, index) => ({
            ...column,
            id: `default_col_${index}_${column.key}`,
        })),
    };
};

// "Hoy 19:20", "Ayer 19:20", "20/09 19:20" y "20/09/25 19:20" para otros años. Se arma a mano y no con Intl para que
// la hora salga siempre en 24 h, sin depender del idioma del navegador.
const formatCompactDateTime = (parsed, reference = new Date()) => {
    const pad = (value) => `${value}`.padStart(2, '0');
    const time = `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;

    const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const daysApart = Math.round((startOfDay(reference) - startOfDay(parsed)) / 86400000);
    if (daysApart === 0) return `Hoy ${time}`;
    if (daysApart === 1) return `Ayer ${time}`;

    const dayMonth = `${pad(parsed.getDate())}/${pad(parsed.getMonth() + 1)}`;
    if (parsed.getFullYear() === reference.getFullYear()) return `${dayMonth} ${time}`;
    return `${dayMonth}/${pad(parsed.getFullYear() % 100)} ${time}`;
};

const formatTableDateTime = (value, format = 'compact') => {
    if (value === null || value === undefined || value === '') return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return `${value}`;
    if (format === 'iso') return parsed.toISOString();
    if (format === 'compact') return formatCompactDateTime(parsed);
    // Locale fijo: con el del navegador los días salían en inglés y la hora en AM/PM.
    return parsed.toLocaleString('es-CL', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
};

const isControlLimitViolation = (value, rawLimits) => {
    const limits = parseControlLimits(rawLimits);
    if (!limits.enabled) return false;

    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return false;

    const minOk = limits.min === null || numeric >= limits.min;
    const maxOk = limits.max === null || numeric <= limits.max;
    const isInside = minOk && maxOk;
    const complies = limits.mode === 'inside' ? isInside : !isInside;
    return !complies;
};

const buildControlLimitMarkLine = (rawLimits) => {
    const limits = parseControlLimits(rawLimits);
    if (!limits.enabled) return null;

    const lines = [];
    const color = limits.mode === 'inside' ? '#F59E0B' : '#EF4444';

    if (limits.min !== null) lines.push({ yAxis: limits.min });
    if (limits.max !== null) lines.push({ yAxis: limits.max });

    if (!lines.length) return null;

    return {
        silent: true,
        symbol: 'none',
        label: { show: false },
        lineStyle: {
            color,
            type: 'dashed',
            width: 1.4,
            opacity: 0.95,
        },
        data: lines,
    };
};

const dateRangeSelectValue = (dateRangeState) => {
    const normalized = normalizeDateRangeState(dateRangeState);
    if (normalized.mode === 'custom') return DATE_RANGE_CUSTOM_KEY;
    return normalized.preset || DEFAULT_RELATIVE_PRESET;
};

const updateDateRangeFromSelect = (dateRangeState, selectedValue) => {
    if (selectedValue === DATE_RANGE_CUSTOM_KEY) {
        return {
            mode: 'custom',
            preset: '',
            from: dateRangeState?.from || '',
            to: dateRangeState?.to || '',
        };
    }

    return {
        mode: 'relative',
        preset: QUICK_RANGE_BY_KEY[selectedValue] ? selectedValue : DEFAULT_RELATIVE_PRESET,
        from: '',
        to: '',
    };
};

const describeDateRangeState = (dateRangeState) => {
    const state = normalizeDateRangeState(dateRangeState);
    if (state.mode === 'relative') {
        return QUICK_RANGE_BY_KEY[state.preset]?.label || QUICK_RANGE_BY_KEY[DEFAULT_RELATIVE_PRESET].label;
    }

    const from = state.from ? new Date(state.from).toLocaleString() : '...';
    const to = state.to ? new Date(state.to).toLocaleString() : '...';
    return `${from} - ${to}`;
};

const normalizeStartOfDay = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    date.setHours(0, 0, 0, 0);
    return date;
};

const normalizeEndOfDay = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    date.setHours(23, 59, 59, 999);
    return date;
};

const formatCompactDate = (value) => {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';

    return date.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const describeCustomDateRangeCompact = (dateRangeState) => {
    const state = normalizeDateRangeState(dateRangeState);
    if (state.mode !== 'custom') return '';
    return `${formatCompactDate(state.from)} - ${formatCompactDate(state.to)}`;
};

const describeDashboardDateRangeCompact = (dateRangeState) => {
    const state = normalizeDateRangeState(dateRangeState);
    if (state.mode === 'relative') {
        return QUICK_RANGE_BY_KEY[state.preset]?.label || QUICK_RANGE_BY_KEY[DEFAULT_RELATIVE_PRESET].label;
    }

    return `${formatCompactDate(state.from)} - ${formatCompactDate(state.to)}`;
};

const toDateTimeInput = (value) => {
    if (!value) return '';
    try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        const iso = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
        return iso.slice(0, 16);
    } catch (_error) {
        return '';
    }
};

const toISOStringOrNull = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString();
};

const normalizeWidgetUnit = (value) => `${value || ''}`.trim();
const parseBooleanLike = (value) => value === true || value === 1 || `${value}`.toLowerCase() === 'true' || `${value}` === '1';
const resolveMetricDecimals = (value) => {
    if (_.isNil(value) || value === '') return DEFAULT_METRIC_DECIMALS;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return DEFAULT_METRIC_DECIMALS;
    return Math.min(MAX_METRIC_DECIMALS, Math.max(0, Math.round(parsed)));
};

const formatMetricNumber = (value, decimals = DEFAULT_METRIC_DECIMALS) => {
    if (_.isNil(value) || value === '') return '-';
    const resolvedDecimals = resolveMetricDecimals(decimals);
    const formatNumeric = (numericValue) => {
        if (Number.isInteger(numericValue)) {
            return numericValue.toLocaleString(undefined, { maximumFractionDigits: 0 });
        }
        return numericValue.toLocaleString(undefined, {
            minimumFractionDigits: resolvedDecimals,
            maximumFractionDigits: resolvedDecimals,
        });
    };

    if (_.isNumber(value)) {
        if (!Number.isFinite(value)) return `${value}`;
        return formatNumeric(value);
    }

    if (_.isString(value)) {
        const text = value.trim();
        if (!text) return '-';
        const numeric = Number(text);
        if (Number.isFinite(numeric) && /^-?\d+(\.\d+)?$/.test(text)) {
            return formatNumeric(numeric);
        }
        return value;
    }

    return `${value}`;
};

const formatMetricWithUnit = (value, unit, decimals = DEFAULT_METRIC_DECIMALS) => {
    const formatted = formatMetricNumber(value, decimals);
    if (formatted === '-') return formatted;

    const normalizedUnit = normalizeWidgetUnit(unit);
    return normalizedUnit ? `${formatted} ${normalizedUnit}` : formatted;
};

const formatTooltipDateTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return `${value}`;

    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = `${date.getFullYear()}`;
    const hours = `${date.getHours()}`.padStart(2, '0');
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    const seconds = `${date.getSeconds()}`.padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

const formatDurationShort = (ms) => {
    if (!Number.isFinite(ms) || ms <= 0) return '0m';
    const totalMinutes = Math.floor(ms / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (totalMinutes >= 1) return `${minutes}m`;
    return `${Math.max(Math.round(ms / 1000), 1)}s`;
};

const computeLaneUptimeStats = (segments, rangeMin, rangeMax) => {
    if (!Array.isArray(segments) || !segments.length) return null;
    const min = Number.isFinite(rangeMin) ? rangeMin : Math.min(...segments.map((segment) => segment[0]));
    const max = Number.isFinite(rangeMax) ? rangeMax : Math.max(...segments.map((segment) => segment[1]));
    if (!(max > min)) return null;

    let onMs = 0;
    let transitions = 0;
    let prevState = null;
    segments.forEach((segment) => {
        const state = Number(segment[3]) >= 1 ? 1 : 0;
        const clampedStart = Math.max(segment[0], min);
        const clampedEnd = Math.min(segment[1], max);
        if (state === 1 && clampedEnd > clampedStart) onMs += clampedEnd - clampedStart;
        if (prevState !== null && state !== prevState) transitions += 1;
        prevState = state;
    });

    const totalMs = max - min;
    return {
        onMs,
        totalMs,
        uptimePct: Math.round((onMs / totalMs) * 100),
        transitions,
    };
};

const buildClonedWidgetTitle = (title, sectionWidgets = []) => {
    const baseTitle = `${title || 'Widget'}`.trim() || 'Widget';
    const normalizedExistingTitles = new Set(
        (sectionWidgets || [])
            .map((widget) => `${widget?.title || ''}`.trim().toLowerCase())
            .filter(Boolean)
    );

    const firstCandidate = `${baseTitle} (copia)`;
    if (!normalizedExistingTitles.has(firstCandidate.toLowerCase())) {
        return firstCandidate;
    }

    let copyIndex = 2;
    let nextCandidate = `${baseTitle} (copia ${copyIndex})`;
    while (normalizedExistingTitles.has(nextCandidate.toLowerCase())) {
        copyIndex += 1;
        nextCandidate = `${baseTitle} (copia ${copyIndex})`;
    }

    return nextCandidate;
};

const normalizeEntity = (payload) => {
    if (!payload) return null;

    if (payload?.data?.id && payload?.data?.attributes) {
        return {
            id: payload.data.id,
            ...payload.data.attributes,
        };
    }

    if (payload?.id && payload?.attributes) {
        return {
            id: payload.id,
            ...payload.attributes,
        };
    }

    return payload;
};

const normalizeWidgetPayload = (payload) => {
    const candidates = [
        payload?.dashboard_widget,
        payload?.widget,
        payload?.data?.dashboard_widget,
        payload?.data?.widget,
        payload?.data,
        payload,
    ];

    return normalizeEntity(candidates.find((candidate) => candidate)) || {};
};

const normalizeCollection = (payload) => {
    if (Array.isArray(payload)) {
        return payload.map((item) => normalizeEntity(item)).filter(Boolean);
    }

    if (Array.isArray(payload?.data)) {
        return payload.data.map((item) => normalizeEntity(item)).filter(Boolean);
    }

    if (Array.isArray(payload?.items)) {
        return payload.items.map((item) => normalizeEntity(item)).filter(Boolean);
    }

    if (Array.isArray(payload?.records)) {
        return payload.records.map((item) => normalizeEntity(item)).filter(Boolean);
    }

    return [];
};

const normalizeEditOptionsPayload = (response) => {
    let payload = response;

    if (payload?.data && !Array.isArray(payload.data)) {
        payload = payload.data;
    }

    if (payload?.attributes && _.isObject(payload.attributes)) {
        payload = payload.attributes;
    }

    payload = _.isObject(payload) ? payload : {};

    const filterOptions = _.isObject(payload.filter_options) ? payload.filter_options : {};

    return {
        ...payload,
        stores: normalizeCollection(payload.stores),
        devices: normalizeCollection(payload.devices),
        filter_options: {
            stores: normalizeCollection(filterOptions.stores),
            zones: normalizeCollection(filterOptions.zones),
            kinds: normalizeCollection(filterOptions.kinds),
            devices: normalizeCollection(filterOptions.devices),
        },
    };
};

const normalizeWidget = (widget, fallbackPosition = 1) => {
    const normalized = normalizeEntity(widget) || {};
    const layout = normalized.layout || {};

    return {
        id: normalized.id,
        title: normalized.title || 'Widget',
        widget_type: canonicalWidgetType(normalized.widget_type || 'timeseries'),
        dashboard_section_id: normalized.dashboard_section_id,
        position: normalized.position || fallbackPosition,
        enabled: normalized.enabled !== false,
        layout: {
            ...DEFAULT_LAYOUT,
            ...layout,
            x: _.isNumber(layout.x) ? layout.x : DEFAULT_LAYOUT.x,
            y: _.isNumber(layout.y) ? layout.y : DEFAULT_LAYOUT.y,
            w: _.isNumber(layout.w) ? layout.w : DEFAULT_LAYOUT.w,
            h: _.isNumber(layout.h) ? layout.h : DEFAULT_LAYOUT.h,
        },
        query_config: normalized.query_config || {},
        viz_config: normalized.viz_config || {},
    };
};

const normalizeSection = (section, fallbackPosition = 1) => {
    const normalized = normalizeEntity(section) || {};
    const widgets = _.orderBy(
        _.map(normalized.dashboard_widgets || [], (widget, index) => normalizeWidget(widget, index + 1)),
        ['position', 'id'],
        ['asc', 'asc']
    );

    return {
        id: normalized.id,
        dashboard_id: normalized.dashboard_id,
        title: normalized.title || 'Sección',
        position: normalized.position || fallbackPosition,
        settings: normalized.settings || {},
        dashboard_widgets: widgets,
    };
};

const normalizeDashboard = (dashboard) => {
    const normalized = normalizeEntity(dashboard) || {};
    const sections = _.orderBy(
        _.map(normalized.dashboard_sections || [], (section, index) => normalizeSection(section, index + 1)),
        ['position', 'id'],
        ['asc', 'asc']
    );

    return {
        id: normalized.id,
        company_id: normalized.company_id,
        name: normalized.name || 'Dashboard sin nombre',
        description: normalized.description || '',
        position: normalized.position || 1,
        filters_config: normalized.filters_config || {},
        settings: normalized.settings || {},
        dashboard_sections: sections,
    };
};

const isPersistedRecordId = (id) => Number(id) > 0;

// Mensaje legible de un error de la API, para no mostrar avisos genéricos que esconden la causa.
const describeApiError = (error) => {
    const data = error?.response?.data;
    const detail = data?.errors || data?.error || data?.message;

    if (Array.isArray(detail) && detail.length) return detail.join(', ');
    if (typeof detail === 'string' && detail.trim()) return detail.trim();

    return error?.message || 'error desconocido';
};

const applySectionLayoutToDashboard = (dashboard, sectionId, nextLayout = []) => {
    if (!dashboard || !sectionId || !Array.isArray(nextLayout) || !nextLayout.length) return dashboard;

    const sorted = _.sortBy(nextLayout, ['y', 'x']);
    const positionById = sorted.reduce((acc, item, index) => {
        const widgetId = Number(item.i);
        if (!Number.isNaN(widgetId)) acc[widgetId] = index + 1;
        return acc;
    }, {});
    const layoutById = nextLayout.reduce((acc, item) => {
        const widgetId = Number(item.i);
        if (!Number.isNaN(widgetId)) {
            acc[widgetId] = {
                x: item.x,
                y: item.y,
                w: item.w,
                h: item.h,
            };
        }
        return acc;
    }, {});

    return {
        ...dashboard,
        dashboard_sections: (dashboard.dashboard_sections || []).map((currentSection) => {
            if (Number(currentSection.id) !== Number(sectionId)) return currentSection;

            const updatedWidgets = (currentSection.dashboard_widgets || []).map((widget) => {
                const widgetId = Number(widget.id);
                const nextWidgetLayout = layoutById[widgetId];
                if (!nextWidgetLayout) return widget;

                return {
                    ...widget,
                    position: positionById[widgetId] || widget.position || 1,
                    layout: {
                        ...(widget.layout || {}),
                        ...nextWidgetLayout,
                    },
                };
            });

            return {
                ...currentSection,
                dashboard_widgets: _.orderBy(updatedWidgets, ['position', 'id'], ['asc', 'asc']),
            };
        }),
    };
};

const appendWidgetToDashboard = (dashboard, sectionId, widget) => {
    if (!dashboard || !sectionId || !widget?.id) return dashboard;

    return {
        ...dashboard,
        dashboard_sections: (dashboard.dashboard_sections || []).map((entry) => {
            if (Number(entry.id) !== Number(sectionId)) return entry;

            const existingWidgets = entry.dashboard_widgets || [];
            const hasWidget = existingWidgets.some((currentWidget) => Number(currentWidget.id) === Number(widget.id));
            const nextWidgets = hasWidget
                ? existingWidgets.map((currentWidget) => (
                    Number(currentWidget.id) === Number(widget.id) ? widget : currentWidget
                ))
                : [...existingWidgets, widget];

            return {
                ...entry,
                dashboard_widgets: _.orderBy(nextWidgets, ['position', 'id'], ['asc', 'asc']),
            };
        }),
    };
};

const buildEmptyDashboardFilters = () => ({
    store_id: '',
    zone: '',
    kind: '',
    device_id: '',
});

const buildDashboardFiltersPayload = (dashboardFilters) => {
    const state = {
        ...buildEmptyDashboardFilters(),
        ...(dashboardFilters || {}),
    };
    const filters = [];

    if (`${state.store_id || ''}`.trim()) {
        filters.push({
            field: 'store_id',
            operator: 'eq',
            value: Number(state.store_id),
        });
    }

    if (`${state.zone || ''}`.trim()) {
        filters.push({
            field: 'zone',
            operator: 'eq',
            value: `${state.zone}`.trim(),
        });
    }

    if (`${state.kind || ''}`.trim()) {
        filters.push({
            field: 'kind',
            operator: 'eq',
            value: `${state.kind}`.trim(),
        });
    }

    if (`${state.device_id || ''}`.trim()) {
        filters.push({
            field: 'device_id',
            operator: 'eq',
            value: Number(state.device_id),
        });
    }

    return filters;
};

const extractFiltersState = (filtersConfig) => {
    const config = filtersConfig || {};
    const rawFilters = Array.isArray(config.filters) ? config.filters : [];
    const dashboardFilters = { ...buildEmptyDashboardFilters() };

    DASHBOARD_FILTER_KEYS.forEach((fieldKey) => {
        const found = rawFilters.find((filter) => `${filter?.field || ''}` === fieldKey);
        if (!found) return;
        const rawValue = Array.isArray(found?.value) ? found.value[0] : found?.value;
        if (rawValue === null || rawValue === undefined) return;
        dashboardFilters[fieldKey] = `${rawValue}`;
    });

    const dateRange = extractDateRangeState(config.date_range || {});

    return {
        dashboardFilters,
        dateRange,
    };
};

const buildWidgetPayload = (widgetForm) => {
    const queryConfig = { ...(widgetForm.query_config || {}) };
    const vizConfig = { ...(widgetForm.viz_config || {}) };

    queryConfig.date_range_source = queryConfig.date_range_source === 'fixed' ? 'fixed' : 'dashboard';
    queryConfig.date_range = buildDateRangePayload(extractDateRangeState(queryConfig.date_range || {}));
    queryConfig.y_metrics = normalizeMetricEntriesForDataset(resolveRawQueryMetrics(queryConfig));
    if (queryConfig.y_metrics.length) {
        queryConfig.y = queryConfig.y_metrics[0].field;
    } else {
        delete queryConfig.y;
    }
    delete queryConfig.bucket;

    vizConfig.unit = normalizeWidgetUnit(vizConfig.unit);
    vizConfig.decimals = resolveMetricDecimals(vizConfig.decimals);
    vizConfig.show_kpi_background = parseBooleanLike(vizConfig.show_kpi_background);
    vizConfig.control_limits = parseControlLimits(vizConfig.control_limits || {});
    vizConfig.y_axis_min = parseAxisLimit(vizConfig.y_axis_min);
    vizConfig.y_axis_max = parseAxisLimit(vizConfig.y_axis_max);
    vizConfig.table_view = normalizeTableViewConfig(vizConfig.table_view || {}, queryConfig.y_metrics || []);

    return {
        title: widgetForm.title,
        widget_type: canonicalWidgetType(widgetForm.widget_type),
        dashboard_section_id: widgetForm.dashboard_section_id,
        position: widgetForm.position,
        enabled: widgetForm.enabled !== false,
        layout: widgetForm.layout || DEFAULT_LAYOUT,
        query_config: queryConfig,
        viz_config: vizConfig,
    };
};

const buildDefaultWidgetForm = (sectionId, editOptions) => {
    const datasets = editOptions?.datasets || {};
    const datasetKey = datasets.device_metrics ? 'device_metrics' : Object.keys(datasets)[0] || '';
    const datasetFields = datasets[datasetKey]?.fields || [];
    const defaultX = datasets[datasetKey]?.default_x || datasetFields.find((field) => field.x)?.key || '';
    const firstMetricField = datasetFields.find((field) => field.metric)?.key || '';
    const firstMetricLabel = firstMetricField ? getMetricLabelFromDatasetFields(datasetFields, firstMetricField) : '';
    const defaultLabelField = findDatasetDefaultLabelField(datasets, datasetKey);

    return {
        id: null,
        title: 'Nuevo widget',
        widget_type: 'timeseries',
        dashboard_section_id: sectionId,
        position: 1,
        enabled: true,
        layout: { ...DEFAULT_LAYOUT },
        query_config: {
            dataset: datasetKey,
            x: defaultX,
            y_metrics: firstMetricField ? [{ field: firstMetricField, label: firstMetricLabel }] : [],
            aggregation: 'avg',
            group_by: [],
            label_field: defaultLabelField,
            date_range_source: 'dashboard',
            date_range: buildDefaultDateRangeState(),
            filters: [],
        },
        viz_config: {
            x_label: '',
            y_label: '',
            series_labels: {},
            unit: '',
            decimals: DEFAULT_METRIC_DECIMALS,
            y_axis_min: null,
            y_axis_max: null,
            table_view: buildDefaultTableViewConfig(firstMetricLabel ? [{ label: firstMetricLabel }] : []),
            show_kpi_background: false,
            control_limits: {
                enabled: false,
                mode: 'inside',
                min: null,
                max: null,
            },
        },
    };
};

const WidgetResultViewComponent = ({
    widget,
    result,
    loading,
    error,
    mapApiLoaded,
    mapEnabled,
    dashboardDateRangeState,
    fillHeight = false,
}) => {
    const widgetType = canonicalWidgetType(widget?.widget_type);
    const widgetVisual = getWidgetVisualMeta(widgetType);
    const controlLimits = parseControlLimits(widget?.viz_config?.control_limits || {});
    const effectiveDateRangeState = getEffectiveWidgetDateRange(widget, dashboardDateRangeState);
    const effectiveAbsoluteDateRange = resolveDateRangeAbsolute(effectiveDateRangeState);
    const xAxisMin = effectiveAbsoluteDateRange.from ? effectiveAbsoluteDateRange.from.getTime() : undefined;
    const xAxisMax = effectiveAbsoluteDateRange.to ? effectiveAbsoluteDateRange.to.getTime() : undefined;
    const rangeDurationMs = (xAxisMin !== undefined && xAxisMax !== undefined) ? (xAxisMax - xAxisMin) : null;
    const shouldShowSmallPoints = rangeDurationMs !== null && rangeDurationMs <= 60 * 60 * 1000;
    const chartHeight = fillHeight ? '100%' : 290;
    const mapHeight = fillHeight ? '100%' : '280px';
    const metricUnit = normalizeWidgetUnit(widget?.viz_config?.unit);
    const metricDecimals = resolveMetricDecimals(widget?.viz_config?.decimals);
    const yAxisMinOverride = parseAxisLimit(widget?.viz_config?.y_axis_min);
    const yAxisMaxOverride = parseAxisLimit(widget?.viz_config?.y_axis_max);
    const showKpiBackground = parseBooleanLike(widget?.viz_config?.show_kpi_background);
    const hasAnimatedInitialDataRef = useRef(false);

    const hasResult = !_.isNil(result);
    const shouldAnimateChart = hasResult && !hasAnimatedInitialDataRef.current;

    useEffect(() => {
        if (hasResult) {
            hasAnimatedInitialDataRef.current = true;
        }
    }, [hasResult, result]);

    if (loading && !hasResult) {
        return (
            <Box
                sx={{
                    py: fillHeight ? 0 : 3,
                    height: fillHeight ? '100%' : 'auto',
                    minHeight: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                }}>
                <CircularProgress size={22} />
            </Box>
        );
    }

    if (error && !hasResult) {
        return <Alert severity="error">No se pudo cargar el widget</Alert>;
    }

    if (!hasResult) {
        return <Typography color="text.secondary">Sin datos todavía</Typography>;
    }

    if (widgetType === 'stat') {
        const hasViolation = isControlLimitViolation(result?.stat?.value, controlLimits);
        const history = Array.isArray(result?.stat?.history) ? result.stat.history : [];
        const hasHistory = history.length > 1;
        const historyData = history.map((point) => [point.x, point.y]);
        const lastHistoryPoint = historyData.length ? historyData[historyData.length - 1] : null;
        const sparklineOption = {
            animation: shouldAnimateChart,
            animationDuration: shouldAnimateChart ? 650 : 0,
            animationDurationUpdate: shouldAnimateChart ? 350 : 0,
            grid: { top: 10, right: 0, bottom: 10, left: 0 },
            xAxis: {
                type: 'time',
                show: false,
            },
            yAxis: {
                type: 'value',
                show: false,
                scale: true,
            },
            tooltip: { show: false },
            series: [
                {
                    type: 'line',
                    smooth: true,
                    symbol: 'none',
                    lineStyle: {
                        width: 0,
                    },
                    areaStyle: {
                        color: {
                            type: 'linear',
                            x: 0,
                            y: 0,
                            x2: 0,
                            y2: 1,
                            colorStops: [
                                { offset: 0, color: alpha(widgetVisual.accent, 0.22) },
                                { offset: 1, color: alpha(widgetVisual.accent, 0.0) },
                            ],
                        },
                    },
                    data: historyData,
                    z: 1,
                },
                {
                    type: 'line',
                    smooth: true,
                    symbol: 'none',
                    lineStyle: {
                        width: 2,
                        color: alpha(widgetVisual.accent, 0.26),
                        shadowBlur: 14,
                        shadowColor: alpha(widgetVisual.accent, 0.24),
                    },
                    data: historyData,
                    z: 2,
                },
                {
                    type: 'line',
                    smooth: true,
                    symbol: 'none',
                    lineStyle: {
                        width: 1.2,
                        color: alpha(widgetVisual.accent, 0.58),
                    },
                    markPoint: lastHistoryPoint ? {
                        symbol: 'circle',
                        symbolSize: 8,
                        itemStyle: {
                            color: alpha(widgetVisual.accent, 0.78),
                            shadowBlur: 10,
                            shadowColor: alpha(widgetVisual.accent, 0.34),
                        },
                        data: [{ coord: lastHistoryPoint }],
                    } : undefined,
                    data: historyData,
                    z: 3,
                },
            ],
        };

        return (
            <Box
                sx={{
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                }}>
                {showKpiBackground && hasHistory && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            pointerEvents: 'none',
                            zIndex: 0,
                        }}>
                        <ReactECharts
                            option={sparklineOption}
                            style={{ height: '100%', width: '100%' }}
                            notMerge={false}
                            replaceMerge={['series']}
                            lazyUpdate
                        />
                    </Box>
                )}

                <Stack
                    spacing={1.1}
                    sx={{
                        height: '100%',
                        justifyContent: 'flex-start',
                        position: 'relative',
                        zIndex: 1,
                        px: 0.25,
                        pt: 1.25,
                    }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Box
                            sx={{
                                px: 0.7,
                                py: 0.25,
                                borderRadius: 1.15,
                                backgroundColor: hasViolation
                                    ? alpha('#FEE2E2', 0.74)
                                    : alpha('#FFFFFF', 0.68),
                            }}>
                            <Stack direction="row" alignItems="baseline" spacing={0.55}>
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontSize: '3rem',
                                        fontWeight: 700,
                                        letterSpacing: -0.5,
                                        color: hasViolation ? 'error.main' : widgetVisual.accent,
                                        lineHeight: 1,
                                    }}>
                                    {formatMetricNumber(result?.stat?.value, metricDecimals)}
                                </Typography>
                                {metricUnit && (
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontSize: '1.35rem',
                                            color: alpha('#0F172A', 0.72),
                                            fontWeight: 700,
                                            letterSpacing: 0.2,
                                            lineHeight: 1,
                                        }}>
                                        {metricUnit}
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    </Stack>
                </Stack>
            </Box>
        );
    }

    if (widgetType === 'table') {
        const rows = Array.isArray(result?.rows) ? result.rows : [];
        if (!rows.length) {
            return <Typography color="text.secondary">Sin filas para mostrar</Typography>;
        }

        const availableColumns = Object.keys(rows[0]);
        const tableView = normalizeTableViewConfig(widget?.viz_config?.table_view || {}, widget?.query_config?.y_metrics || []);
        const configuredColumns = (tableView.columns || [])
            .filter((column) => availableColumns.includes(column.key));
        const columns = configuredColumns.length
            ? configuredColumns
            : availableColumns.map((columnKey) => ({
                id: `fallback_${columnKey}`,
                key: columnKey,
                label: _.startCase(columnKey.replace(/_/g, ' ')),
                type: inferTableColumnType(columnKey),
            }));
        const metricColumnSet = new Set((widget?.query_config?.y_metrics || []).map((_metric, index) => `metric_${index}`));
        return (
            <Box
                sx={{
                    maxHeight: fillHeight ? '100%' : 260,
                    height: fillHeight ? '100%' : 'auto',
                    minHeight: 0,
                    overflow: 'auto',
                }}>
                <Table
                    size="small"
                    stickyHeader
                    sx={{
                        // La tabla heredaba los 14px de MUI y se veía más grande que el resto de la card; el nowrap
                        // evita que una fecha se parta en tres líneas en el teléfono.
                        '& .MuiTableCell-root': {
                            fontSize: { xs: '0.72rem', md: '0.8125rem' },
                            py: 0.4,
                            px: 0.9,
                            whiteSpace: 'nowrap',
                        },
                        '& .MuiTableCell-head': {
                            fontWeight: 700,
                            color: 'text.secondary',
                        },
                    }}>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell key={column.id || column.key}>{column.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.slice(0, 80).map((row, rowIndex) => (
                            <TableRow key={`table-row-${rowIndex}`}>
                                {columns.map((column) => {
                                    const isMetricColumn = metricColumnSet.has(column.key);
                                    const hasViolation = isMetricColumn && isControlLimitViolation(row[column.key], controlLimits);
                                    const rawValue = row[column.key];
                                    let displayValue = `${rawValue ?? ''}`;

                                    if (column.type === 'datetime') {
                                        displayValue = formatTableDateTime(rawValue, tableView.datetime_format);
                                    } else if (column.type === 'number') {
                                        displayValue = formatMetricNumber(rawValue, metricDecimals);
                                    }

                                    return (
                                        <TableCell
                                            key={`${rowIndex}-${column.id || column.key}`}
                                            sx={hasViolation ? { backgroundColor: 'rgba(211, 47, 47, 0.12)', color: 'error.main', fontWeight: 600 } : undefined}>
                                            {displayValue}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        );
    }

    if (widgetType === 'map') {
        const points = Array.isArray(result?.map_points) ? result.map_points : [];

        if (!mapEnabled) {
            return <Alert severity="info">Configura `REACT_APP_GOOGLE_MAPS_API_KEY` para habilitar el mapa.</Alert>;
        }

        if (!mapApiLoaded) {
            return (
                <Box
                    sx={{
                        py: fillHeight ? 0 : 3,
                        height: fillHeight ? '100%' : 'auto',
                        minHeight: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                    }}>
                    <CircularProgress size={22} />
                </Box>
            );
        }

        if (!points.length) {
            return <Typography color="text.secondary">Sin puntos geográficos para mostrar</Typography>;
        }

        const first = points[0];
        const center = {
            lat: Number(first.lat) || 0,
            lng: Number(first.lng) || 0,
        };

        return (
            <GoogleMap
                mapContainerStyle={{ width: '100%', height: mapHeight }}
                center={center}
                zoom={12}
                options={{ streetViewControl: false, mapTypeControl: false }}>
                {points.map((point, index) => (
                    <Marker
                        key={`map-point-${index}`}
                        position={{
                            lat: Number(point.lat) || 0,
                            lng: Number(point.lng) || 0,
                        }}
                        title={point.label || `Punto ${index + 1}`}
                    />
                ))}
            </GoogleMap>
        );
    }

    if (chartWidgets.has(widgetType)) {
        const series = Array.isArray(result?.series) ? result.series : [];

        const isStateTimeline = widgetType === 'state_timeline';
        const isBarChart = widgetType === 'bar';
        const supportsLimitLine = widgetType === 'timeseries' || widgetType === 'bar';
        const markLine = supportsLimitLine ? buildControlLimitMarkLine(controlLimits) : null;

        const toTimestamp = (value) => {
            if (_.isNil(value) || value === '') return null;

            const numeric = Number(value);
            if (Number.isFinite(numeric)) return numeric;

            const parsed = new Date(value).getTime();
            return Number.isFinite(parsed) ? parsed : null;
        };

        const barXAxisPaddingMs = (() => {
            if (!isBarChart) return 0;

            const diffs = series.flatMap((serie) => {
                const timestamps = (Array.isArray(serie?.data) ? serie.data : [])
                    .map((point) => toTimestamp(point?.x))
                    .filter((value) => Number.isFinite(value))
                    .sort((a, b) => a - b);

                return timestamps
                    .slice(1)
                    .map((timestamp, index) => timestamp - timestamps[index])
                    .filter((diff) => Number.isFinite(diff) && diff > 0);
            });

            const minDiff = diffs.length ? Math.min(...diffs) : null;
            if (Number.isFinite(minDiff) && minDiff > 0) {
                return Math.max(Math.round(minDiff / 2), 60 * 1000);
            }

            if (rangeDurationMs !== null && rangeDurationMs > 0) {
                return Math.max(Math.round(rangeDurationMs / 120), 5 * 60 * 1000);
            }

            return 5 * 60 * 1000;
        })();
        const chartXAxisMin = Number.isFinite(xAxisMin)
            ? (isBarChart ? xAxisMin - barXAxisPaddingMs : xAxisMin)
            : undefined;
        const chartXAxisMax = Number.isFinite(xAxisMax)
            ? (isBarChart ? xAxisMax + barXAxisPaddingMs : xAxisMax)
            : undefined;

        if (isStateTimeline) {
            const STATE_ON_COLOR = '#10B981';
            const STATE_OFF_COLOR = '#E2E8F0';
            const STATE_OFF_BORDER_COLOR = '#CBD5E1';
            const laneRecords = series.map((serie, seriesIndex) => {
                const name = `${serie?.name || `Serie ${seriesIndex + 1}`}`;
                const points = (Array.isArray(serie?.data) ? serie.data : [])
                    .map((point) => {
                        const timestamp = toTimestamp(point?.x);
                        if (!Number.isFinite(timestamp)) return null;

                        return {
                            timestamp,
                            state: Number(point?.y) >= 1 ? 1 : 0,
                        };
                    })
                    .filter(Boolean)
                    .sort((a, b) => a.timestamp - b.timestamp);

                return {
                    name,
                    laneIndex: seriesIndex,
                    points,
                };
            });

            const timelineValues = laneRecords.flatMap((record) => record.points.map((point) => point.timestamp));
            const computedMin = timelineValues.length ? Math.min(...timelineValues) : null;
            const computedMax = timelineValues.length ? Math.max(...timelineValues) : null;
            const timelineMin = Number.isFinite(xAxisMin) ? xAxisMin : computedMin;
            const timelineMax = Number.isFinite(xAxisMax) ? xAxisMax : computedMax;
            const timelineHasRange = Number.isFinite(timelineMin) && Number.isFinite(timelineMax) && timelineMax > timelineMin;
            const fallbackSegmentMs = timelineHasRange
                ? Math.max(Math.round((timelineMax - timelineMin) / 240), 60 * 1000)
                : 60 * 1000;
            const hasSingleLane = laneRecords.length === 1;

            const laneSegments = laneRecords.map((record) => {
                // El backend serializa el tiempo a resolución de segundo: dos muestras
                // en el mismo segundo llegan con timestamp idéntico. Sin dedupe se
                // fabricarían segmentos solapados que inflan el uptime sobre 100%.
                const points = [];
                record.points.forEach((point) => {
                    if (points.length && points[points.length - 1].timestamp === point.timestamp) {
                        points[points.length - 1] = point;
                    } else {
                        points.push(point);
                    }
                });
                if (!points.length) {
                    return { ...record, segmentData: [] };
                }

                if (timelineHasRange && points[0].timestamp > timelineMin) {
                    points.unshift({
                        timestamp: timelineMin,
                        state: points[0].state,
                    });
                }

                const segmentData = points
                    .map((point, pointIndex) => {
                        const nextPoint = points[pointIndex + 1];
                        const end = nextPoint
                            ? nextPoint.timestamp
                            : (timelineHasRange ? Math.max(timelineMax, point.timestamp) : (point.timestamp + fallbackSegmentMs));

                        return [point.timestamp, end, record.laneIndex, point.state];
                    })
                    .filter((segment) => segment[1] > segment[0]);

                return { ...record, segmentData };
            });

            const laneStats = laneSegments.map((record) => (
                computeLaneUptimeStats(record.segmentData, timelineMin, timelineMax)
            ));

            const timelineSeries = laneSegments.map((record) => {
                if (!record.segmentData.length) {
                    return {
                        name: record.name,
                        type: 'custom',
                        encode: { x: [0, 1], y: 2 },
                        data: [],
                    };
                }

                return {
                    name: record.name,
                    type: 'custom',
                    clip: true,
                    encode: { x: [0, 1], y: 2 },
                    data: record.segmentData,
                    renderItem: (params, api) => {
                        const lane = api.value(2);
                        const stateValue = Number(api.value(3)) >= 1 ? 1 : 0;
                        const start = api.coord([api.value(0), lane]);
                        const end = api.coord([api.value(1), lane]);
                        const laneHeight = Math.abs(api.size([0, 1])[1]);
                        const barHeight = hasSingleLane
                            ? Math.max(laneHeight * 1.02, 24)
                            : Math.max(Math.min(laneHeight * 0.92, 40), 14);
                        const rawWidth = end[0] - start[0];
                        const width = Math.max(Math.abs(rawWidth), 1);
                        const x = rawWidth >= 0 ? start[0] : end[0];

                        const coordSys = params.coordSys;
                        const clippedX = Math.max(x, coordSys.x);
                        const clippedRight = Math.min(x + width, coordSys.x + coordSys.width);
                        const clippedWidth = clippedRight - clippedX;
                        if (clippedWidth <= 0) return null;
                        const snappedX = Math.round(clippedX);
                        const snappedRight = Math.round(clippedX + clippedWidth);
                        const snappedWidth = Math.max(snappedRight - snappedX, 1);

                        const y = hasSingleLane
                            ? (coordSys.y + coordSys.height - barHeight - 1)
                            : (start[1] - (barHeight / 2) + (laneHeight * 0.12));
                        const clippedY = Math.max(y, coordSys.y);
                        const clippedBottom = Math.min(y + barHeight, coordSys.y + coordSys.height);
                        const clippedHeight = clippedBottom - clippedY;
                        if (clippedHeight <= 0) return null;

                        const isOn = stateValue === 1;
                        const drawHeight = isOn
                            ? clippedHeight
                            : Math.min(Math.max(clippedHeight * 0.6, 8), clippedHeight);
                        const drawY = clippedY + ((clippedHeight - drawHeight) / 2);
                        const cornerRadius = Math.min(3, snappedWidth / 2, drawHeight / 2);

                        return {
                            type: 'rect',
                            shape: {
                                x: snappedX,
                                y: drawY,
                                width: snappedWidth,
                                height: drawHeight,
                                r: cornerRadius,
                            },
                            style: {
                                fill: isOn ? STATE_ON_COLOR : STATE_OFF_COLOR,
                                stroke: isOn ? 'transparent' : STATE_OFF_BORDER_COLOR,
                                lineWidth: isOn ? 0 : 1,
                            },
                            emphasis: {
                                style: {
                                    shadowBlur: 6,
                                    shadowColor: 'rgba(15, 23, 42, 0.28)',
                                },
                            },
                            transition: shouldAnimateChart ? ['shape'] : [],
                        };
                    },
                    emphasis: { disabled: false },
                };
            });

            const hasManyLanes = laneRecords.length > 1;
            const hasTimelineData = timelineSeries.some((timelineItem) => (
                Array.isArray(timelineItem?.data) && timelineItem.data.length > 0
            ));
            const stateLegendGraphic = [
                {
                    type: 'group',
                    left: 12,
                    top: 8,
                    silent: true,
                    children: [
                        {
                            type: 'rect',
                            shape: { x: 0, y: 2, width: 12, height: 12, r: 4 },
                            style: { fill: STATE_ON_COLOR, stroke: 'transparent' },
                        },
                        {
                            type: 'text',
                            style: {
                                x: 18,
                                y: 8,
                                text: 'ON',
                                fill: '#64748B',
                                fontSize: 12,
                                fontWeight: 500,
                                textAlign: 'left',
                                textVerticalAlign: 'middle',
                            },
                        },
                        {
                            type: 'rect',
                            shape: { x: 60, y: 2, width: 12, height: 12, r: 4 },
                            style: { fill: STATE_OFF_COLOR, stroke: STATE_OFF_BORDER_COLOR, lineWidth: 1 },
                        },
                        {
                            type: 'text',
                            style: {
                                x: 78,
                                y: 8,
                                text: 'OFF',
                                fill: '#64748B',
                                fontSize: 12,
                                fontWeight: 500,
                                textAlign: 'left',
                                textVerticalAlign: 'middle',
                            },
                        },
                    ],
                },
            ];
            const singleLaneStats = hasSingleLane ? laneStats[0] : null;
            // El resumen va bajo la leyenda, no pegado al borde derecho: anclado a los dos bordes se traslapaba
            // con la leyenda apenas el widget se angostaba.
            const singleLaneSummaryGraphic = singleLaneStats ? [{
                type: 'text',
                left: 12,
                top: 28,
                silent: true,
                style: {
                    text: `ON ${singleLaneStats.uptimePct}% · ${formatDurationShort(singleLaneStats.onMs)} · ${singleLaneStats.transitions} ${singleLaneStats.transitions === 1 ? 'cambio' : 'cambios'}`,
                    fill: '#475569',
                    fontSize: 12,
                    fontWeight: 600,
                    textAlign: 'left',
                },
            }] : [];
            const option = {
                animation: shouldAnimateChart,
                animationDuration: shouldAnimateChart ? 650 : 0,
                animationDurationUpdate: shouldAnimateChart ? 350 : 0,
                grid: {
                    // Con resumen hay dos líneas sobre el gráfico (leyenda y resumen).
                    top: singleLaneStats ? 58 : 40,
                    right: 20,
                    bottom: 40,
                    left: hasManyLanes ? 16 : 20,
                    containLabel: hasManyLanes,
                },
                tooltip: {
                    trigger: 'item',
                    // En el body: dentro del widget (overflow hidden) un tooltip con muchas series queda cortado.
                    appendTo: 'body',
                    backgroundColor: 'rgba(15, 23, 42, 0.94)',
                    borderWidth: 0,
                    textStyle: {
                        color: '#F8FAFC',
                        fontSize: 12,
                    },
                    extraCssText: 'box-shadow: 0 10px 28px rgba(15, 23, 42, 0.28); border-radius: 10px; padding: 10px 12px;',
                    formatter: (param) => {
                        const value = Array.isArray(param?.value) ? param.value : [];
                        const from = value[0];
                        const to = value[1];
                        const hasFrom = Number.isFinite(Number(from));
                        const hasTo = Number.isFinite(Number(to));
                        const fromLabel = hasFrom ? formatTooltipDateTime(from) : '--';
                        const toLabel = hasTo ? formatTooltipDateTime(to) : '--';
                        const stateLabel = Number(value[3]) >= 1 ? 'ON' : 'OFF';
                        const color = Number(value[3]) >= 1 ? STATE_ON_COLOR : STATE_OFF_COLOR;
                        const durationLabel = (hasFrom && hasTo && Number(to) > Number(from))
                            ? formatDurationShort(Number(to) - Number(from))
                            : null;

                        return (
                            '<div style="display:flex;flex-direction:column;gap:4px;line-height:1.2;">'
                            + `<div style="color:#CBD5E1;font-size:11px;">${fromLabel}</div>`
                            + `<div style="display:flex;align-items:center;gap:6px;color:#E2E8F0;font-size:12px;">`
                            + `<span style="display:inline-block;width:8px;height:8px;border-radius:999px;background:${color};"></span>`
                            + `<span>${param?.seriesName || ''}: <b style="color:#F8FAFC">${stateLabel}</b></span>`
                            + '</div>'
                            + `<div style="color:#94A3B8;font-size:11px;">Hasta ${toLabel}</div>`
                            + (durationLabel
                                ? `<div style="color:#94A3B8;font-size:11px;">Duración: <b style="color:#E2E8F0">${durationLabel}</b></div>`
                                : '')
                            + '</div>'
                        );
                    },
                },
                legend: { show: false },
                xAxis: {
                    type: 'time',
                    min: timelineHasRange ? timelineMin : undefined,
                    max: timelineHasRange ? timelineMax : undefined,
                    axisLabel: {
                        hideOverlap: true,
                        color: '#64748B',
                        fontSize: 11,
                        formatter: {
                            year: '{yyyy}',
                            month: '{MMM}',
                            day: '{d} {MMM}',
                            hour: '{HH}:{mm}',
                            minute: '{HH}:{mm}',
                            second: '{HH}:{mm}:{ss}',
                        },
                    },
                    axisTick: { show: false },
                    axisLine: {
                        lineStyle: {
                            color: alpha('#334155', 0.45),
                        },
                    },
                    splitLine: {
                        lineStyle: {
                            color: alpha('#94A3B8', 0.22),
                            type: 'dashed',
                        },
                    },
                },
                yAxis: {
                    type: 'category',
                    data: laneRecords.map((record) => record.name),
                    axisLabel: {
                        show: hasManyLanes,
                        margin: 12,
                        formatter: (value, index) => {
                            const stats = laneStats[index];
                            if (!stats) return `{name|${value}}`;
                            const changesLabel = stats.transitions === 1 ? 'cambio' : 'cambios';
                            return `{name|${value}}\n{stats|${stats.uptimePct}% ON · ${stats.transitions} ${changesLabel}}`;
                        },
                        rich: {
                            name: {
                                color: '#475569',
                                fontSize: 11,
                                fontWeight: 600,
                                align: 'right',
                                lineHeight: 15,
                                width: 132,
                                overflow: 'truncate',
                            },
                            stats: {
                                color: '#94A3B8',
                                fontSize: 10,
                                align: 'right',
                                lineHeight: 13,
                            },
                        },
                    },
                    axisLine: { show: false },
                    axisTick: { show: false },
                    splitLine: { show: false },
                },
                series: timelineSeries,
                graphic: [
                    ...stateLegendGraphic,
                    ...singleLaneSummaryGraphic,
                    ...(!hasTimelineData ? [{
                        type: 'text',
                        left: 'center',
                        top: 'middle',
                        style: {
                            text: 'Sin datos en el rango seleccionado',
                            fill: '#64748B',
                            fontSize: 13,
                        },
                    }] : []),
                ],
            };

            return (
                <ReactECharts
                    option={option}
                    style={{ height: chartHeight, width: '100%' }}
                    notMerge={false}
                    replaceMerge={['series', 'graphic']}
                    lazyUpdate
                />
            );
        }

        const option = {
            color: CHART_SERIES_COLORS,
            animation: shouldAnimateChart,
            animationDuration: shouldAnimateChart ? 650 : 0,
            animationDurationUpdate: shouldAnimateChart ? 350 : 0,
            grid: {
                top: 46,
                right: 20,
                bottom: 40,
                left: 52,
            },
            tooltip: {
                trigger: 'axis',
                // En el body: dentro del widget (overflow hidden) un tooltip con muchas series queda cortado.
                appendTo: 'body',
                backgroundColor: 'rgba(15, 23, 42, 0.94)',
                borderWidth: 0,
                textStyle: {
                    color: '#F8FAFC',
                    fontSize: 12,
                },
                extraCssText: 'box-shadow: 0 10px 28px rgba(15, 23, 42, 0.28); border-radius: 10px; padding: 10px 12px;',
                formatter: (params) => {
                    const entries = Array.isArray(params) ? params : [params];
                    if (!entries.length) return '';

                    const header = formatTooltipDateTime(entries[0]?.axisValue);
                    const metricLines = entries.map((entry) => {
                        const value = Array.isArray(entry?.value) ? entry.value[1] : entry?.value;
                        const displayValue = formatMetricWithUnit(value, metricUnit, metricDecimals);
                        return (
                            `<div style="display:flex;align-items:center;gap:6px;color:#E2E8F0;font-size:12px;line-height:1.25;">`
                            + `${entry?.marker || ''}`
                            + `<span>${entry?.seriesName || ''}: <b style="color:#F8FAFC">${displayValue}</b></span>`
                            + '</div>'
                        );
                    });

                    return (
                        '<div style="display:flex;flex-direction:column;gap:3px;line-height:1.2;">'
                        + `<div style="color:#CBD5E1;font-size:11px;line-height:1.2;">${header}</div>`
                        + metricLines.join('')
                        + '</div>'
                    );
                },
            },
            legend: {
                top: 6,
                // Acotada a la banda que queda a la derecha del eje: centrada se cruzaba con la unidad del eje Y.
                left: 62,
                right: 8,
                type: 'scroll',
                icon: 'circle',
                itemWidth: 10,
                itemHeight: 10,
                textStyle: {
                    color: '#64748B',
                    fontSize: 12,
                },
            },
                xAxis: {
                    type: 'time',
                    min: chartXAxisMin,
                    max: chartXAxisMax,
                    axisLabel: {
                        hideOverlap: true,
                        color: '#64748B',
                        fontSize: 11,
                },
                axisLine: {
                    lineStyle: {
                        color: alpha('#334155', 0.45),
                    },
                },
                splitLine: {
                    lineStyle: {
                        color: alpha('#94A3B8', 0.22),
                        type: 'dashed',
                    },
                },
            },
            yAxis: {
                type: 'value',
                name: metricUnit || undefined,
                min: yAxisMinOverride ?? undefined,
                max: yAxisMaxOverride ?? undefined,
                // Sin esto ECharts dibuja la unidad centrada sobre el eje y a 15px del borde, desalineada de sus
                // etiquetas y encima de la leyenda.
                nameLocation: 'end',
                nameGap: 10,
                nameTextStyle: {
                    color: '#64748B',
                    fontSize: 11,
                    fontWeight: 600,
                    align: 'right',
                    verticalAlign: 'bottom',
                    padding: [0, 0, 0, 0],
                },
                axisLabel: {
                    formatter: (value) => formatMetricNumber(value, metricDecimals),
                    color: '#64748B',
                    fontSize: 11,
                },
                splitLine: {
                    lineStyle: {
                        color: alpha('#94A3B8', 0.24),
                        type: 'dashed',
                    },
                },
            },
            series: series.map((serie, index) => ({
                name: serie.name,
                type: isBarChart ? 'bar' : 'line',
                smooth: !isBarChart,
                step: false,
                showSymbol: !isBarChart && shouldShowSmallPoints,
                symbolSize: !isBarChart ? 2 : undefined,
                emphasis: { focus: 'series' },
                markLine: index === 0 ? markLine : undefined,
                lineStyle: {
                    width: isBarChart ? undefined : 1.6,
                },
                itemStyle: {
                    borderColor: '#FFFFFF',
                    borderWidth: !isBarChart ? 1 : 0,
                },
                areaStyle: !isBarChart && !markLine ? {
                    color: {
                        type: 'linear',
                        x: 0,
                        y: 0,
                        x2: 0,
                        y2: 1,
                        colorStops: [
                            { offset: 0, color: alpha(CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length], 0.35) },
                            { offset: 1, color: alpha(CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length], 0.02) },
                        ],
                    },
                } : undefined,
                barMaxWidth: isBarChart ? 16 : undefined,
                barCategoryGap: isBarChart ? '28%' : undefined,
                barGap: isBarChart ? '10%' : undefined,
                data: (serie.data || []).map((point) => [point.x, point.y]),
            })),
            graphic: !series.length
                ? [{
                    type: 'text',
                    left: 'center',
                    top: 'middle',
                    style: {
                        text: 'Sin datos en el rango seleccionado',
                        fill: '#64748B',
                        fontSize: 13,
                    },
                }]
                : [],
        };

        return (
            <ReactECharts
                option={option}
                style={{ height: chartHeight, width: '100%' }}
                notMerge={false}
                replaceMerge={['series', 'graphic']}
                lazyUpdate
            />
        );
    }

    return <Typography color="text.secondary">Tipo de widget no soportado</Typography>;
};

const WidgetResultView = React.memo(WidgetResultViewComponent);

const DashboardsIndex = () => {
    const theme = useTheme();
    const isMobileViewport = useMediaQuery(theme.breakpoints.down('md'));
    const { authUser } = useJumboAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const canEdit = hasAnyProfile(authUser, EDITOR_ROLES);
    const mapEnabled = Boolean(GOOGLE_MAPS_API_KEY);

    const { isLoaded: mapApiLoaded } = useLoadScript({
        googleMapsApiKey: GOOGLE_MAPS_API_KEY || '',
        id: 'dashboard-builder-maps',
    });

    const [dashboards, setDashboards] = useState([]);
    const [dashboardsLoading, setDashboardsLoading] = useState(false);
    const [selectedDashboardId, setSelectedDashboardId] = useState(null);
    const [selectedDashboard, setSelectedDashboard] = useState(null);
    const [dashboardLoading, setDashboardLoading] = useState(false);
    const [editOptions, setEditOptions] = useState(null);

    const [runtimeDashboardFilters, setRuntimeDashboardFilters] = useState(buildEmptyDashboardFilters());
    const [runtimeDateRange, setRuntimeDateRange] = useState(buildDefaultDateRangeState());
    const [runtimeAutoRefresh, setRuntimeAutoRefresh] = useState(DEFAULT_AUTO_REFRESH_INTERVAL);
    const [filtersCollapsed, setFiltersCollapsed] = useState(true);
    const [mobileFiltersAnchor, setMobileFiltersAnchor] = useState(null);
    const [customDateRangeAnchorEl, setCustomDateRangeAnchorEl] = useState(null);
    const [customDateRangeDraft, setCustomDateRangeDraft] = useState([null, null]);
    const [dashboardPickerAnchorEl, setDashboardPickerAnchorEl] = useState(null);
    const [dashboardActionsAnchorEl, setDashboardActionsAnchorEl] = useState(null);
    const [createDashboardDialogOpen, setCreateDashboardDialogOpen] = useState(false);
    const [createDashboardDialogMode, setCreateDashboardDialogMode] = useState('create');
    const [createDashboardName, setCreateDashboardName] = useState('Nuevo dashboard');
    const [creatingDashboard, setCreatingDashboard] = useState(false);
    const [savingFilters, setSavingFilters] = useState(false);
    const [assistantDraftDirty, setAssistantDraftDirty] = useState(false);
    const [savingAssistantDraft, setSavingAssistantDraft] = useState(false);

    const [widgetResults, setWidgetResults] = useState({});
    const [isEditMode, setIsEditMode] = useState(false);
    const [busySectionId, setBusySectionId] = useState(null);
    const skipNextBulkWidgetQueryRef = useRef(false);
    const skipNextSelectedDashboardLoadRef = useRef(false);
    const widgetQueryRunRef = useRef(0);
    const autoRefreshPendingWhileHiddenRef = useRef(false);
    const assistantTempIdRef = useRef(-1);
    const pendingAssistantDraftRef = useRef(null);
    const dashboardsLoadedRef = useRef(false);
    const [pendingAssistantDraftKey, setPendingAssistantDraftKey] = useState(null);

    const [widgetDialogOpen, setWidgetDialogOpen] = useState(false);
    const [widgetForm, setWidgetForm] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewResult, setPreviewResult] = useState(null);

    const operatorOptions = editOptions?.operators || DEFAULT_OPERATORS;
    const aggregationOptions = editOptions?.aggregations || DEFAULT_AGGREGATIONS;
    const autoRefreshOptions = useMemo(
        () => normalizeAutoRefreshOptions(editOptions?.auto_refresh?.options),
        [editOptions?.auto_refresh?.options]
    );
    const autoRefreshDefault = useMemo(() => {
        const normalizedDefault = normalizeAutoRefreshInterval(
            editOptions?.auto_refresh?.default,
            DEFAULT_AUTO_REFRESH_INTERVAL
        );

        if (autoRefreshOptions.some((option) => option.value === normalizedDefault)) {
            return normalizedDefault;
        }

        return autoRefreshOptions[0]?.value || DEFAULT_AUTO_REFRESH_INTERVAL;
    }, [editOptions?.auto_refresh?.default, autoRefreshOptions]);
    const activeAutoRefreshValue = useMemo(() => {
        const normalized = normalizeAutoRefreshInterval(runtimeAutoRefresh, autoRefreshDefault);
        if (autoRefreshOptions.some((option) => option.value === normalized)) return normalized;
        return autoRefreshOptions[0]?.value || DEFAULT_AUTO_REFRESH_INTERVAL;
    }, [runtimeAutoRefresh, autoRefreshDefault, autoRefreshOptions]);
    const widgetTypeOptions = useMemo(() => {
        const fromApi = editOptions?.widget_types || EDITOR_WIDGET_TYPES;
        const normalized = fromApi.map((type) => canonicalWidgetType(type));
        const allowed = normalized.filter((type) => EDITOR_WIDGET_TYPES.includes(type));
        return _.uniq(allowed);
    }, [editOptions?.widget_types]);
    const datasetsMap = useMemo(() => editOptions?.datasets || {}, [editOptions]);

    const deviceCatalog = useMemo(() => {
        const records = Array.isArray(editOptions?.devices) ? editOptions.devices : [];
        return records.map((device) => ({
            value: `${device.id}`,
            label: device.name || device.code || `Device ${device.id}`,
            zone: device.zone || '',
            kind: device.kind || '',
            store_id: device.store_id === null || device.store_id === undefined ? '' : `${device.store_id}`,
            store_name: device.store_name || '',
        }));
    }, [editOptions?.devices]);

    const storeFilterOptions = useMemo(() => {
        const explicitOptions = _.sortBy(
            (Array.isArray(editOptions?.filter_options?.stores) ? editOptions.filter_options.stores : [])
                .map((store) => ({ value: `${store.id}`, label: store.label || store.name })),
            ['label']
        );
        if (explicitOptions.length) return explicitOptions;

        const stores = _.sortBy(
            (Array.isArray(editOptions?.stores) ? editOptions.stores : [])
                .map((store) => ({ value: `${store.id}`, label: store.name })),
            ['label']
        );
        if (stores.length) return stores;

        const fallback = _.uniqBy(
            deviceCatalog
                .filter((device) => device.store_id && device.store_name)
                .map((device) => ({ value: `${device.store_id}`, label: device.store_name })),
            'value'
        );
        return _.sortBy(fallback, ['label']);
    }, [editOptions?.stores, editOptions?.filter_options?.stores, deviceCatalog]);

    const zoneFilterOptions = useMemo(() => {
        const explicitOptions = _.sortBy(
            (Array.isArray(editOptions?.filter_options?.zones) ? editOptions.filter_options.zones : [])
                .map((zone) => ({ value: `${zone.id}`, label: zone.label || zone.id })),
            ['label']
        );
        if (explicitOptions.length) return explicitOptions;

        return _.uniq(deviceCatalog.map((device) => device.zone).filter(Boolean))
            .sort((a, b) => a.localeCompare(b))
            .map((zone) => ({ value: zone, label: zone }));
    }, [deviceCatalog, editOptions?.filter_options?.zones]);

    const kindFilterOptions = useMemo(() => {
        const explicitOptions = _.sortBy(
            (Array.isArray(editOptions?.filter_options?.kinds) ? editOptions.filter_options.kinds : [])
                .map((kind) => ({ value: `${kind.id}`, label: kind.label || kind.id })),
            ['label']
        );
        if (explicitOptions.length) return explicitOptions;

        return _.uniq(deviceCatalog.map((device) => device.kind).filter(Boolean))
            .sort((a, b) => a.localeCompare(b))
            .map((kind) => ({ value: kind, label: kind }));
    }, [deviceCatalog, editOptions?.filter_options?.kinds]);

    const deviceFilterOptions = useMemo(() => {
        let filtered = (Array.isArray(editOptions?.filter_options?.devices) ? editOptions.filter_options.devices : []).map((device) => ({
            value: `${device.id}`,
            label: device.label || device.name || `Device ${device.id}`,
            zone: device.zone || '',
            kind: device.kind || '',
            store_id: device.store_id === null || device.store_id === undefined ? '' : `${device.store_id}`,
        }));
        if (!filtered.length) {
            filtered = [...deviceCatalog];
        }

        if (runtimeDashboardFilters.store_id) {
            filtered = filtered.filter((device) => device.store_id === `${runtimeDashboardFilters.store_id}`);
        }
        if (runtimeDashboardFilters.zone) {
            filtered = filtered.filter((device) => device.zone === runtimeDashboardFilters.zone);
        }
        if (runtimeDashboardFilters.kind) {
            filtered = filtered.filter((device) => device.kind === runtimeDashboardFilters.kind);
        }

        return _.sortBy(filtered, ['label']);
    }, [
        editOptions?.filter_options?.devices,
        deviceCatalog,
        runtimeDashboardFilters.store_id,
        runtimeDashboardFilters.zone,
        runtimeDashboardFilters.kind,
    ]);

    const selectedSections = useMemo(() => {
        return _.orderBy(selectedDashboard?.dashboard_sections || [], ['position', 'id'], ['asc', 'asc']);
    }, [selectedDashboard]);
    const widgetsErrorCount = useMemo(
        () => Object.values(widgetResults || {}).filter((item) => item?.error && _.isNil(item?.data)).length,
        [widgetResults]
    );
    const activeFixedFilterCount = useMemo(
        () => DASHBOARD_FILTER_KEYS.reduce((acc, key) => (runtimeDashboardFilters?.[key] ? acc + 1 : acc), 0),
        [runtimeDashboardFilters]
    );
    const activeFiltersCount = useMemo(
        () => activeFixedFilterCount + (runtimeDateRange.mode === 'custom' ? 1 : 0),
        [activeFixedFilterCount, runtimeDateRange.mode]
    );
    const activeRangeLabel = useMemo(
        () => describeDashboardDateRangeCompact(runtimeDateRange),
        [runtimeDateRange]
    );
    const customRangeCompactLabel = useMemo(
        () => describeCustomDateRangeCompact(runtimeDateRange),
        [runtimeDateRange]
    );
    const customDateRangeEditorOpen = Boolean(customDateRangeAnchorEl);
    const dashboardPickerOpen = Boolean(dashboardPickerAnchorEl);
    const dashboardActionsOpen = Boolean(dashboardActionsAnchorEl);

    const loadEditOptions = useCallback(async () => {
        try {
            const response = await dashboardServices.getEditOptions();
            const normalizedResponse = normalizeEditOptionsPayload(response);
            setEditOptions(normalizedResponse);
        } catch (_error) {}
    }, []);

    const loadDashboards = useCallback(async (preferredDashboardId = null) => {
        setDashboardsLoading(true);
        try {
            const response = await dashboardServices.getDashboards('?page=1');
            const records = Array.isArray(response?.data) ? response.data : [];
            const normalized = _.orderBy(records.map((record) => normalizeDashboard(record)), ['position', 'id'], ['asc', 'asc']);

            setDashboards(normalized);

            const nextId = preferredDashboardId && normalized.some((dashboard) => `${dashboard.id}` === `${preferredDashboardId}`)
                ? preferredDashboardId
                : normalized[0]?.id;

            setSelectedDashboardId(nextId || null);
            if (!nextId) {
                widgetQueryRunRef.current += 1;
                setSelectedDashboard(null);
                setWidgetResults({});
                setRuntimeAutoRefresh(DEFAULT_AUTO_REFRESH_INTERVAL);
            }
        } catch (_error) {}
        dashboardsLoadedRef.current = true;
        setDashboardsLoading(false);
    }, []);

    const queryWidgets = useCallback(async (dashboard, filters, dateRange) => {
        const queryRunId = widgetQueryRunRef.current + 1;
        widgetQueryRunRef.current = queryRunId;

        if (!dashboard?.id) {
            setWidgetResults({});
            return;
        }

        const widgets = (dashboard.dashboard_sections || [])
            .flatMap((section) => section.dashboard_widgets || [])
            .filter((widget) => isPersistedRecordId(widget.id));
        // Los widgets de un borrador sin guardar (por ejemplo, recién aplicado desde el asistente) no tienen id y muestran
        // su vista previa: el refresco no los consulta, pero tampoco puede borrar esos resultados. Antes los dejaba en
        // «Sin datos todavía» al primer refresco automático.
        const draftResults = (prev) => _.pickBy(prev, (_record, widgetId) => !isPersistedRecordId(widgetId));
        if (!widgets.length) {
            setWidgetResults((prev) => draftResults(prev));
            return;
        }

        setWidgetResults((prev) => widgets.reduce((acc, widget) => {
            const previousRecord = prev?.[widget.id] || {};
            const hasPreviousData = !_.isNil(previousRecord?.data);

            acc[widget.id] = {
                ...previousRecord,
                loading: !hasPreviousData,
                refreshing: true,
                data: hasPreviousData ? previousRecord.data : null,
                error: hasPreviousData ? null : previousRecord?.error || null,
            };
            return acc;
        }, draftResults(prev)));

        const dateRangePayload = buildDateRangePayload(dateRange);

        await Promise.all(
            widgets.map(async (widget) => {
                try {
                    const response = await dashboardServices.queryWidget(dashboard.id, widget.id, {
                        dashboard_filters: { filters },
                        dashboard_date_range: dateRangePayload,
                    });

                    if (widgetQueryRunRef.current !== queryRunId) return;

                    setWidgetResults((prev) => {
                        if (widgetQueryRunRef.current !== queryRunId) return prev;

                        return {
                            ...prev,
                            [widget.id]: {
                                ...(prev?.[widget.id] || {}),
                                loading: false,
                                refreshing: false,
                                data: response,
                                error: null,
                            },
                        };
                    });
                } catch (_error) {
                    if (widgetQueryRunRef.current !== queryRunId) return;

                    setWidgetResults((prev) => {
                        if (widgetQueryRunRef.current !== queryRunId) return prev;

                        const previousRecord = prev?.[widget.id] || {};
                        const hasPreviousData = !_.isNil(previousRecord?.data);

                        return {
                            ...prev,
                            [widget.id]: {
                                ...previousRecord,
                                loading: false,
                                refreshing: false,
                                data: hasPreviousData ? previousRecord.data : null,
                                error: hasPreviousData ? null : true,
                            },
                        };
                    });
                }
            })
        );
    }, []);

    const loadDashboardById = useCallback(async (dashboardId) => {
        if (!dashboardId) return;

        widgetQueryRunRef.current += 1;
        setDashboardLoading(true);
        try {
            const response = await dashboardServices.getDashboard(dashboardId);
            const dashboard = normalizeDashboard(response);

            setSelectedDashboard(dashboard);
            const filtersState = extractFiltersState(dashboard.filters_config);
            setRuntimeDashboardFilters(filtersState.dashboardFilters);
            setRuntimeDateRange(filtersState.dateRange);
            setRuntimeAutoRefresh(normalizeAutoRefreshInterval(dashboard?.settings?.auto_refresh, autoRefreshDefault));
        } catch (_error) {
            setSelectedDashboard(null);
            setWidgetResults({});
        }
        setDashboardLoading(false);
    }, [autoRefreshDefault]);

    const refreshDashboardWidgets = useCallback(() => {
        if (!selectedDashboard?.id || dashboardLoading || busySectionId) return false;

        queryWidgets(selectedDashboard, buildDashboardFiltersPayload(runtimeDashboardFilters), runtimeDateRange);
        return true;
    }, [
        selectedDashboard,
        dashboardLoading,
        busySectionId,
        queryWidgets,
        runtimeDashboardFilters,
        runtimeDateRange,
    ]);

    useEffect(() => {
        loadEditOptions();
        loadDashboards();
    }, [loadDashboards, loadEditOptions]);

    useEffect(() => {
        if (!selectedDashboardId) return;
        if (skipNextSelectedDashboardLoadRef.current) {
            skipNextSelectedDashboardLoadRef.current = false;
            return;
        }
        loadDashboardById(selectedDashboardId);
    }, [selectedDashboardId, loadDashboardById]);

    useEffect(() => {
        if (!selectedDashboard?.id || dashboardLoading || busySectionId) return;
        if (skipNextBulkWidgetQueryRef.current) {
            skipNextBulkWidgetQueryRef.current = false;
            return;
        }

        const timer = setTimeout(() => {
            refreshDashboardWidgets();
        }, 150);

        return () => clearTimeout(timer);
    }, [selectedDashboard, runtimeDashboardFilters, runtimeDateRange, dashboardLoading, refreshDashboardWidgets, busySectionId]);

    useEffect(() => {
        if (!selectedDashboard?.id || dashboardLoading || busySectionId) return;

        const intervalMs = AUTO_REFRESH_INTERVAL_MS[activeAutoRefreshValue] || 0;
        if (!intervalMs) return;

        const intervalId = setInterval(() => {
            if (typeof document !== 'undefined' && document.hidden) {
                autoRefreshPendingWhileHiddenRef.current = true;
                return;
            }

            refreshDashboardWidgets();
        }, intervalMs);

        return () => clearInterval(intervalId);
    }, [
        selectedDashboard,
        dashboardLoading,
        busySectionId,
        activeAutoRefreshValue,
        refreshDashboardWidgets,
    ]);

    useEffect(() => {
        if (typeof document === 'undefined') return undefined;

        const handleVisibilityChange = () => {
            if (document.hidden || !autoRefreshPendingWhileHiddenRef.current) return;

            if (refreshDashboardWidgets()) {
                autoRefreshPendingWhileHiddenRef.current = false;
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        handleVisibilityChange();

        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [refreshDashboardWidgets]);

    const refreshSelectedDashboard = useCallback(async () => {
        if (!selectedDashboardId) return;
        await loadDashboardById(selectedDashboardId);
        await loadDashboards(selectedDashboardId);
    }, [loadDashboardById, loadDashboards, selectedDashboardId]);

    const handleCreateDashboard = () => {
        if (!canEdit) return;
        setCreateDashboardDialogMode('create');
        setCreateDashboardName('Nuevo dashboard');
        setCreateDashboardDialogOpen(true);
    };

    const closeCreateDashboardDialog = () => {
        if (creatingDashboard) return;
        setCreateDashboardDialogOpen(false);
    };

    const submitCreateDashboard = async () => {
        if (!canEdit || creatingDashboard) return;

        const name = `${createDashboardName || ''}`.trim();
        if (!name) return;

        let submittedOk = false;
        setCreatingDashboard(true);
        try {
            if (createDashboardDialogMode === 'rename') {
                if (!selectedDashboard?.id) {
                    setCreatingDashboard(false);
                    return;
                }
                await dashboardServices.updateDashboard(selectedDashboard.id, {
                    dashboard: {
                        name,
                    },
                });
                await refreshSelectedDashboard();
            } else {
                const created = await dashboardServices.createDashboard({
                    dashboard: {
                        name,
                        description: '',
                        filters_config: {
                            filters: [],
                            date_range: buildDateRangePayload(buildDefaultDateRangeState()),
                        },
                        settings: {
                            auto_refresh: autoRefreshDefault,
                        },
                    },
                });
                const normalized = normalizeDashboard(created);
                if (normalized?.id) {
                    await dashboardServices.createSection(normalized.id, {
                        dashboard_section: {
                            title: 'Sección 1',
                            settings: {},
                        },
                    });
                    await loadDashboards(normalized.id);
                } else {
                    await loadDashboards();
                }
                setCreateDashboardDialogMode('create');
                setCreateDashboardName('Nuevo dashboard');
            }
            submittedOk = true;
        } catch (_error) {}
        setCreatingDashboard(false);
        if (submittedOk) {
            setCreateDashboardDialogOpen(false);
        }
    };

    const handleRenameDashboard = () => {
        if (!selectedDashboard || !canEdit) return;
        if (creatingDashboard) return;
        setCreateDashboardDialogMode('rename');
        setCreateDashboardName(selectedDashboard.name || 'Dashboard');
        setCreateDashboardDialogOpen(true);
    };

    const handleDeleteDashboard = async () => {
        if (!selectedDashboard || !canEdit) return;

        const confirmed = window.confirm(`¿Eliminar dashboard "${selectedDashboard.name}"?`);
        if (!confirmed) return;

        try {
            await dashboardServices.deleteDashboard(selectedDashboard.id);
            await loadDashboards();
        } catch (_error) {}
    };

    const openDashboardPicker = (event) => {
        if (!dashboards.length || dashboardsLoading) return;
        setDashboardPickerAnchorEl(event.currentTarget);
    };

    const closeDashboardPicker = () => {
        setDashboardPickerAnchorEl(null);
    };

    const handleSelectDashboardFromPicker = (dashboardId) => {
        setAssistantDraftDirty(false);
        setSelectedDashboardId(`${dashboardId}`);
        closeDashboardPicker();
    };

    const openDashboardActionsMenu = (event) => {
        if (!canEdit) return;
        setDashboardActionsAnchorEl(event.currentTarget);
    };

    const closeDashboardActionsMenu = () => {
        setDashboardActionsAnchorEl(null);
    };

    const handleToggleEditModeFromMenu = () => {
        setIsEditMode(true);
        closeDashboardActionsMenu();
    };

    const handleCreateDashboardFromMenu = () => {
        closeDashboardActionsMenu();
        handleCreateDashboard();
    };

    const saveAssistantDashboardDraft = useCallback(async () => {
        if (!selectedDashboard?.id || !canEdit || savingAssistantDraft) return;

        setSavingAssistantDraft(true);
        // Se guarda todo lo que se pueda y se reportan los fallos al final: antes, un widget inválido cortaba el
        // guardado y los widgets siguientes se perdían sin aviso.
        const failures = [];
        try {
            await dashboardServices.updateDashboard(selectedDashboard.id, {
                dashboard: {
                    name: selectedDashboard.name || 'Dashboard IA',
                    description: selectedDashboard.description || '',
                    filters_config: selectedDashboard.filters_config || {
                        filters: [],
                        date_range: buildDateRangePayload(buildDefaultDateRangeState()),
                    },
                    settings: selectedDashboard.settings || {
                        auto_refresh: autoRefreshDefault,
                    },
                },
            });

            const orderedSections = _.orderBy(selectedDashboard.dashboard_sections || [], ['position', 'id'], ['asc', 'asc']);

            for (const [sectionIndex, section] of orderedSections.entries()) {
                let persistedSectionId = section.id;
                const sectionTitle = section.title || `Sección ${sectionIndex + 1}`;
                const sectionPayload = {
                    title: sectionTitle,
                    position: section.position || sectionIndex + 1,
                    settings: section.settings || {},
                };

                try {
                    if (isPersistedRecordId(section.id)) {
                        await dashboardServices.updateSection(selectedDashboard.id, section.id, {
                            dashboard_section: sectionPayload,
                        });
                    } else {
                        const createdSection = await dashboardServices.createSection(selectedDashboard.id, {
                            dashboard_section: sectionPayload,
                        });
                        persistedSectionId = normalizeSection(createdSection, sectionIndex + 1)?.id || createdSection?.id;
                    }
                } catch (error) {
                    console.error(`Error guardando la sección "${sectionTitle}"`, error);
                    failures.push(`sección "${sectionTitle}": ${describeApiError(error)}`);
                    continue;
                }

                if (!persistedSectionId) continue;

                const widgets = _.orderBy(section.dashboard_widgets || [], ['position', 'id'], ['asc', 'asc']);
                for (const [widgetIndex, widget] of widgets.entries()) {
                    const widgetTitle = widget.title || `Widget ${widgetIndex + 1}`;
                    const widgetPayload = buildWidgetPayload({
                        ...widget,
                        dashboard_section_id: persistedSectionId,
                        position: widget.position || widgetIndex + 1,
                    });

                    try {
                        if (isPersistedRecordId(widget.id)) {
                            await dashboardServices.updateWidget(selectedDashboard.id, widget.id, {
                                dashboard_widget: widgetPayload,
                            });
                        } else {
                            await dashboardServices.createWidget(selectedDashboard.id, {
                                dashboard_widget: widgetPayload,
                            });
                        }
                    } catch (error) {
                        console.error(`Error guardando el widget "${widgetTitle}"`, error);
                        failures.push(`widget "${widgetTitle}": ${describeApiError(error)}`);
                    }
                }
            }

            if (failures.length) {
                window.alert(`No se pudieron guardar ${failures.length === 1 ? 'estos cambios' : 'algunos cambios'}:\n\n${failures.join('\n')}`);
            } else {
                setAssistantDraftDirty(false);
                setIsEditMode(false);
            }
        } catch (error) {
            console.error('Error guardando los cambios del asistente', error);
            window.alert(`No fue posible guardar los cambios del asistente: ${describeApiError(error)}`);
        } finally {
            // Se refresca siempre, incluso tras un error: si algo alcanzó a guardarse, la vista tiene que mostrarlo.
            // Antes el refresco iba dentro del try y su falla se reportaba como si no se hubiera guardado nada.
            try {
                await refreshSelectedDashboard();
            } catch (refreshError) {
                console.error('Error recargando el dashboard después de guardar', refreshError);
            }
            setSavingAssistantDraft(false);
        }
    }, [
        autoRefreshDefault,
        canEdit,
        refreshSelectedDashboard,
        savingAssistantDraft,
        selectedDashboard,
    ]);

    const handleFinishEditMode = () => {
        if (assistantDraftDirty) {
            saveAssistantDashboardDraft();
            return;
        }
        setIsEditMode(false);
    };

    const handleFinishEditModeFromMenu = () => {
        closeDashboardActionsMenu();
        handleFinishEditMode();
    };

    const handleRenameDashboardFromMenu = () => {
        closeDashboardActionsMenu();
        handleRenameDashboard();
    };

    const handleDeleteDashboardFromMenu = () => {
        closeDashboardActionsMenu();
        handleDeleteDashboard();
    };

    const handleUpdateDashboardFixedFilter = (fieldKey, value) => {
        setRuntimeDashboardFilters((prev) => {
            const next = { ...prev, [fieldKey]: value };

            if (fieldKey !== 'device_id' && next.device_id) {
                const selectedDevice = deviceCatalog.find((device) => device.value === `${next.device_id}`);
                const violatesStore = next.store_id && selectedDevice && selectedDevice.store_id !== `${next.store_id}`;
                const violatesZone = next.zone && selectedDevice && selectedDevice.zone !== next.zone;
                const violatesKind = next.kind && selectedDevice && selectedDevice.kind !== next.kind;

                if (violatesStore || violatesZone || violatesKind) {
                    next.device_id = '';
                }
            }

            return next;
        });
    };

    const saveDashboardFilters = async () => {
        if (!selectedDashboard || !canEdit) return;

        setSavingFilters(true);
        try {
            await dashboardServices.updateDashboard(selectedDashboard.id, {
                dashboard: {
                    filters_config: {
                        filters: buildDashboardFiltersPayload(runtimeDashboardFilters),
                        date_range: buildDateRangePayload(runtimeDateRange),
                    },
                    settings: {
                        ...(selectedDashboard.settings || {}),
                        auto_refresh: activeAutoRefreshValue,
                    },
                },
            });
            await refreshSelectedDashboard();
        } catch (_error) {}
        setSavingFilters(false);
    };

    const handleCreateSection = async () => {
        if (!selectedDashboard || !canEdit) return;

        const title = window.prompt('Título de la sección', `Sección ${(selectedSections.length || 0) + 1}`);
        if (!title?.trim()) return;

        try {
            await dashboardServices.createSection(selectedDashboard.id, {
                dashboard_section: {
                    title: title.trim(),
                    settings: {},
                },
            });
            await refreshSelectedDashboard();
        } catch (_error) {}
    };

    const handleRenameSection = async (section) => {
        if (!selectedDashboard || !canEdit || !section?.id) return;

        const title = window.prompt('Nuevo título de sección', section.title || 'Sección');
        if (!title?.trim()) return;

        try {
            await dashboardServices.updateSection(selectedDashboard.id, section.id, {
                dashboard_section: {
                    title: title.trim(),
                },
            });
            await refreshSelectedDashboard();
        } catch (_error) {}
    };

    const handleDeleteSection = async (section) => {
        if (!selectedDashboard || !canEdit || !section?.id) return;

        const confirmed = window.confirm(`¿Eliminar sección "${section.title}" y todos sus widgets?`);
        if (!confirmed) return;

        try {
            await dashboardServices.deleteSection(selectedDashboard.id, section.id);
            await refreshSelectedDashboard();
        } catch (_error) {}
    };

    const handleMoveSection = async (section, direction) => {
        if (!selectedDashboard || !canEdit || !section?.id) return;

        const ids = selectedSections.map((entry) => entry.id);
        const index = ids.findIndex((id) => `${id}` === `${section.id}`);
        if (index < 0) return;

        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= ids.length) return;

        const next = [...ids];
        [next[index], next[targetIndex]] = [next[targetIndex], next[index]];

        try {
            await dashboardServices.reorderSections(selectedDashboard.id, next);
            await refreshSelectedDashboard();
        } catch (_error) {}
    };

    const openWidgetDialog = (sectionId, widget = null) => {
        if (!selectedDashboard) return;

        if (!widget) {
            setWidgetForm(buildDefaultWidgetForm(sectionId, editOptions));
        } else {
            const normalizedWidget = normalizeWidget(widget, widget.position || 1);
            const normalizedQuery = normalizedWidget.query_config || {};
            const normalizedViz = normalizedWidget.viz_config || {};
            const normalizedDatasetKey = normalizedQuery.dataset || 'device_metrics';
            const normalizedDatasetFields = datasetsMap?.[normalizedDatasetKey]?.fields || [];
            const normalizedRawMetrics = resolveRawQueryMetrics(normalizedQuery);
            setWidgetForm({
                ...normalizedWidget,
                query_config: {
                    dataset: normalizedDatasetKey,
                    x: normalizedQuery.x || '',
                    y_metrics: normalizeMetricEntriesForDataset(normalizedRawMetrics, normalizedDatasetFields),
                    aggregation: normalizedQuery.aggregation || 'avg',
                    group_by: normalizedQuery.group_by || [],
                    label_field: normalizedQuery.label_field || '',
                    filters: normalizedQuery.filters || [],
                    date_range_source: normalizedQuery.date_range_source === 'fixed' ? 'fixed' : 'dashboard',
                    date_range: extractDateRangeState(normalizedQuery.date_range || {}),
                },
                viz_config: {
                    ...normalizedViz,
                    unit: normalizeWidgetUnit(normalizedViz.unit),
                    decimals: resolveMetricDecimals(normalizedViz.decimals),
                    y_axis_min: parseAxisLimit(normalizedViz.y_axis_min),
                    y_axis_max: parseAxisLimit(normalizedViz.y_axis_max),
                    table_view: normalizeTableViewConfig(normalizedViz.table_view || {}, normalizedQuery.y_metrics || []),
                    show_kpi_background: parseBooleanLike(normalizedViz.show_kpi_background),
                    control_limits: parseControlLimits(normalizedViz.control_limits || {}),
                },
            });
        }

        setPreviewResult(null);
        setWidgetDialogOpen(true);
    };

    const closeWidgetDialog = () => {
        setWidgetDialogOpen(false);
        setWidgetForm(null);
        setPreviewResult(null);
    };

    const selectedDatasetFields = useMemo(() => {
        const datasetKey = widgetForm?.query_config?.dataset;
        if (!datasetKey) return [];
        return datasetsMap?.[datasetKey]?.fields || [];
    }, [datasetsMap, widgetForm?.query_config?.dataset]);

    const selectedDatasetMetricFields = useMemo(() => {
        return selectedDatasetFields.filter((field) => field.metric);
    }, [selectedDatasetFields]);

    const selectedDatasetFilterFields = useMemo(() => {
        return selectedDatasetFields.filter((field) => field.filterable);
    }, [selectedDatasetFields]);

    const selectedDatasetGroupFields = useMemo(() => {
        return selectedDatasetFields.filter((field) => field.groupable || field.dimension);
    }, [selectedDatasetFields]);

    const selectedDatasetLabelFields = useMemo(() => {
        return selectedDatasetFields.filter((field) => field.dimension || field.groupable || field.x);
    }, [selectedDatasetFields]);
    const selectedDatasetFieldLabelMap = useMemo(() => {
        return selectedDatasetFields.reduce((acc, field) => {
            acc[field.key] = getFieldDisplayLabel(field);
            return acc;
        }, {});
    }, [selectedDatasetFields]);
    const selectedDatasetMetricLabelMap = useMemo(() => {
        return selectedDatasetMetricFields.reduce((acc, field) => {
            acc[field.key] = getFieldDisplayLabel(field);
            return acc;
        }, {});
    }, [selectedDatasetMetricFields]);
    const widgetMetricLabelMap = useMemo(() => {
        return _.keyBy(widgetForm?.query_config?.y_metrics || [], 'field');
    }, [widgetForm?.query_config?.y_metrics]);
    const counterAggregations = editOptions?.counter_aggregations || DEFAULT_COUNTER_AGGREGATIONS;
    const selectedAggregation = widgetForm?.query_config?.aggregation || '';
    const selectedMetricsAreCounters = useMemo(() => {
        const metrics = widgetForm?.query_config?.y_metrics || [];
        if (!metrics.length) return false;
        const fieldsByKey = _.keyBy(selectedDatasetFields, 'key');
        return metrics.every((metric) => fieldsByKey[metric.field]?.counter);
    }, [selectedDatasetFields, widgetForm?.query_config?.y_metrics]);
    const availableAggregationOptions = useMemo(() => aggregationOptions.filter((aggregation) => (
        !counterAggregations.includes(aggregation)
        || selectedMetricsAreCounters
        || aggregation === selectedAggregation
    )), [aggregationOptions, counterAggregations, selectedMetricsAreCounters, selectedAggregation]);

    const activeWidgetType = canonicalWidgetType(widgetForm?.widget_type || 'timeseries');
    const activeWidgetFormRules = widgetFormRulesFor(activeWidgetType);
    const isStateTimelineWidget = activeWidgetType === 'state_timeline';
    const widgetDateRangeSource = widgetForm?.query_config?.date_range_source === 'fixed' ? 'fixed' : 'dashboard';
    const widgetDateRangeState = normalizeDateRangeState(widgetForm?.query_config?.date_range);
    const widgetControlLimits = parseControlLimits(widgetForm?.viz_config?.control_limits || {});
    const widgetMetricDecimals = resolveMetricDecimals(widgetForm?.viz_config?.decimals);
    const widgetYAxisMin = parseAxisLimit(widgetForm?.viz_config?.y_axis_min);
    const widgetYAxisMax = parseAxisLimit(widgetForm?.viz_config?.y_axis_max);
    const widgetTableView = useMemo(
        () => normalizeTableViewConfig(widgetForm?.viz_config?.table_view || {}, widgetForm?.query_config?.y_metrics || []),
        [widgetForm?.viz_config?.table_view, widgetForm?.query_config?.y_metrics]
    );
    const tableColumnKeyOptions = useMemo(() => {
        const options = [
            { key: 'bucket_time', label: 'bucket_time (Fecha/Hora)' },
            { key: 'device_id', label: 'device_id (ID dispositivo)' },
            { key: 'state', label: 'state (Estado)' },
        ];

        (widgetForm?.query_config?.group_by || []).forEach((fieldKey, index) => {
            const fieldLabel = selectedDatasetFieldLabelMap[fieldKey] || fieldKey;
            options.push({
                key: `group_${index}`,
                label: `group_${index} (${fieldLabel})`,
            });
        });

        (widgetForm?.query_config?.y_metrics || []).forEach((metric, index) => {
            const metricLabel = `${metric?.label || ''}`.trim()
                || selectedDatasetMetricLabelMap[metric?.field]
                || metric?.field
                || `Métrica ${index + 1}`;
            options.push({
                key: `metric_${index}`,
                label: `metric_${index} (${metricLabel})`,
            });
        });

        return _.uniqBy(options, 'key');
    }, [
        widgetForm?.query_config?.group_by,
        widgetForm?.query_config?.y_metrics,
        selectedDatasetFieldLabelMap,
        selectedDatasetMetricLabelMap,
    ]);
    const effectiveWidgetDateRange = useMemo(
        () => getEffectiveWidgetDateRange(widgetForm, runtimeDateRange),
        [widgetForm, runtimeDateRange]
    );
    const effectiveWidgetAutoBucket = useMemo(
        () => computeAutoBucket(effectiveWidgetDateRange, activeWidgetType),
        [effectiveWidgetDateRange, activeWidgetType]
    );

    const handleWidgetDatasetChange = (datasetKey) => {
        if (!widgetForm) return;

        const nextType = canonicalWidgetType(widgetForm.widget_type);
        const defaultX = findDatasetDefaultX(datasetsMap, datasetKey);
        const firstMetric = findFirstMetricField(datasetsMap, datasetKey);
        const defaultLabelField = findDatasetDefaultLabelField(datasetsMap, datasetKey);
        const fields = datasetsMap?.[datasetKey]?.fields || [];
        const hasStateField = fields.some((field) => field.key === 'state' && field.metric);

        setWidgetForm((prev) => {
            let nextMetrics = firstMetric
                ? [{ field: firstMetric, label: getMetricLabelFromDatasetFields(fields, firstMetric) }]
                : [];

            if (nextType === 'state_timeline' && hasStateField) {
                nextMetrics = [{ field: 'state', label: getMetricLabelFromDatasetFields(fields, 'state') }];
            }

            if (nextType === 'map') {
                nextMetrics = [];
            }

            return {
                ...prev,
                query_config: {
                    ...prev.query_config,
                    dataset: datasetKey,
                    x: defaultX,
                    y_metrics: nextMetrics,
                    group_by: [],
                    label_field: defaultLabelField,
                    filters: [],
                },
            };
        });
    };

    const handleWidgetTypeChange = (widgetType) => {
        setWidgetForm((prev) => {
            if (!prev) return prev;

            const nextType = canonicalWidgetType(widgetType);
            let nextDataset = prev.query_config?.dataset || Object.keys(datasetsMap || {})[0] || '';

            if (nextType === 'map') {
                nextDataset = findDatasetForMap(datasetsMap);
            }

            const defaultX = findDatasetDefaultX(datasetsMap, nextDataset);
            const firstMetric = findFirstMetricField(datasetsMap, nextDataset);
            const defaultLabelField = findDatasetDefaultLabelField(datasetsMap, nextDataset);
            const fields = datasetsMap?.[nextDataset]?.fields || [];
            const hasStateField = fields.some((field) => field.key === 'state' && field.metric);
            const hasCurrentLabelField = fields.some((field) => field.key === prev.query_config?.label_field);

            const currentMetricsMap = _.keyBy(prev.query_config?.y_metrics || [], 'field');
            let nextMetrics = prev.query_config?.y_metrics || [];

            if (!nextMetrics.length && firstMetric) {
                nextMetrics = [{ field: firstMetric, label: getMetricLabelFromDatasetFields(fields, firstMetric) }];
            }

            if (nextType === 'state_timeline') {
                if (hasStateField) {
                    nextMetrics = [{
                        field: 'state',
                        label: currentMetricsMap.state?.label || getMetricLabelFromDatasetFields(fields, 'state'),
                    }];
                } else if (firstMetric) {
                    nextMetrics = [{
                        field: firstMetric,
                        label: currentMetricsMap[firstMetric]?.label || getMetricLabelFromDatasetFields(fields, firstMetric),
                    }];
                }
            }

            if (nextType === 'map') {
                nextMetrics = [];
            }

            nextMetrics = normalizeMetricEntriesForDataset(nextMetrics, fields);

            return {
                ...prev,
                widget_type: nextType,
                query_config: {
                    ...prev.query_config,
                    dataset: nextDataset,
                    x: defaultX || prev.query_config?.x || '',
                    y_metrics: nextMetrics,
                    aggregation: nextType === 'stat' ? (prev.query_config?.aggregation || 'avg') : prev.query_config?.aggregation,
                    group_by: nextType === 'stat' || nextType === 'map'
                        ? []
                        : (prev.query_config?.group_by || []),
                    label_field: hasCurrentLabelField ? prev.query_config?.label_field : defaultLabelField,
                },
            };
        });
    };

    const handleWidgetSave = async () => {
        if (!selectedDashboard || !widgetForm || !canEdit) return;
        const widgetType = canonicalWidgetType(widgetForm.widget_type);
        const requiresMetrics = ['timeseries', 'bar', 'state_timeline', 'stat', 'table'].includes(widgetType);

        const limits = parseControlLimits(widgetForm?.viz_config?.control_limits || {});
        if (limits.enabled && limits.min === null && limits.max === null) {
            window.alert('Configura al menos un límite mínimo o máximo cuando habilitas límites de control.');
            return;
        }
        if (limits.enabled && limits.min !== null && limits.max !== null && limits.min > limits.max) {
            window.alert('El límite mínimo no puede ser mayor que el máximo.');
            return;
        }
        if (widgetYAxisMin !== null && widgetYAxisMax !== null && widgetYAxisMin > widgetYAxisMax) {
            window.alert('El mínimo del eje Y no puede ser mayor que el máximo.');
            return;
        }

        const payload = buildWidgetPayload(widgetForm);
        if (requiresMetrics && !(payload.query_config?.y_metrics || []).length) {
            window.alert('Selecciona al menos una métrica Y para guardar el widget.');
            return;
        }

        try {
            if (widgetForm.id) {
                await dashboardServices.updateWidget(selectedDashboard.id, widgetForm.id, {
                    dashboard_widget: payload,
                });
            } else {
                await dashboardServices.createWidget(selectedDashboard.id, {
                    dashboard_widget: payload,
                });
            }

            closeWidgetDialog();
            await refreshSelectedDashboard();
        } catch (error) {
            console.error('Error guardando el widget', error);
            window.alert(`No fue posible guardar el widget: ${describeApiError(error)}`);
        }
    };

    const handleWidgetDelete = async (widget) => {
        if (!selectedDashboard || !widget?.id || !canEdit) return;

        const confirmed = window.confirm(`¿Eliminar widget "${widget.title}"?`);
        if (!confirmed) return;

        try {
            await dashboardServices.deleteWidget(selectedDashboard.id, widget.id);
            await refreshSelectedDashboard();
        } catch (error) {
            console.error('Error eliminando el widget', error);
            window.alert(`No fue posible eliminar el widget: ${describeApiError(error)}`);
        }
    };

    const handleWidgetClone = async (section, widget) => {
        if (!selectedDashboard || !section?.id || !widget?.id || !canEdit) return;

        const sectionWidgets = section.dashboard_widgets || [];
        const normalizedWidget = normalizeWidget(widget, (sectionWidgets.length || 0) + 1);
        const maxBottom = sectionWidgets.reduce((acc, sectionWidget) => {
            const widgetLayout = sectionWidget?.layout || {};
            const y = _.isNumber(widgetLayout.y) ? widgetLayout.y : 0;
            const h = _.isNumber(widgetLayout.h) ? widgetLayout.h : DEFAULT_LAYOUT.h;
            return Math.max(acc, y + h);
        }, 0);

        const currentLayout = normalizedWidget.layout || {};
        const clonedLayout = {
            x: _.isNumber(currentLayout.x) ? currentLayout.x : DEFAULT_LAYOUT.x,
            y: Math.max(maxBottom + 1, _.isNumber(currentLayout.y) ? currentLayout.y + 1 : DEFAULT_LAYOUT.y),
            w: _.isNumber(currentLayout.w) ? currentLayout.w : DEFAULT_LAYOUT.w,
            h: _.isNumber(currentLayout.h) ? currentLayout.h : DEFAULT_LAYOUT.h,
        };

        const cloneWidgetForm = {
            ...normalizedWidget,
            id: null,
            title: buildClonedWidgetTitle(normalizedWidget.title, sectionWidgets),
            dashboard_section_id: section.id,
            position: (sectionWidgets.length || 0) + 1,
            layout: clonedLayout,
            query_config: _.cloneDeep(normalizedWidget.query_config || {}),
            viz_config: _.cloneDeep(normalizedWidget.viz_config || {}),
        };

        try {
            const created = await dashboardServices.createWidget(selectedDashboard.id, {
                dashboard_widget: buildWidgetPayload(cloneWidgetForm),
            });

            const normalizedCreatedWidget = normalizeWidget(
                normalizeWidgetPayload(created),
                (sectionWidgets.length || 0) + 1
            );
            if (!normalizedCreatedWidget?.id) {
                await refreshSelectedDashboard();
                return;
            }

            skipNextBulkWidgetQueryRef.current = true;
            setSelectedDashboard((prev) => appendWidgetToDashboard(prev, section.id, normalizedCreatedWidget));
            setDashboards((prev) => (prev || []).map((dashboard) => (
                Number(dashboard.id) === Number(selectedDashboard.id)
                    ? appendWidgetToDashboard(dashboard, section.id, normalizedCreatedWidget)
                    : dashboard
            )));

            setWidgetResults((prev) => ({
                ...prev,
                [normalizedCreatedWidget.id]: {
                    loading: true,
                    refreshing: true,
                    data: null,
                    error: null,
                },
            }));

            try {
                const response = await dashboardServices.queryWidget(selectedDashboard.id, normalizedCreatedWidget.id, {
                    dashboard_filters: { filters: buildDashboardFiltersPayload(runtimeDashboardFilters) },
                    dashboard_date_range: buildDateRangePayload(runtimeDateRange),
                });

                setWidgetResults((prev) => ({
                    ...prev,
                    [normalizedCreatedWidget.id]: {
                        loading: false,
                        refreshing: false,
                        data: response,
                        error: null,
                    },
                }));
            } catch (_error) {
                setWidgetResults((prev) => ({
                    ...prev,
                    [normalizedCreatedWidget.id]: {
                        loading: false,
                        refreshing: false,
                        data: null,
                        error: true,
                    },
                }));
            }
        } catch (_error) {}
    };

    const nextAssistantTempId = useCallback(() => {
        const nextId = assistantTempIdRef.current;
        assistantTempIdRef.current -= 1;
        return nextId;
    }, []);

    const normalizeAssistantWidgetDraft = useCallback((widgetDraft, sectionId, fallbackPosition = 1) => {
        const widgetType = canonicalWidgetType(widgetDraft?.widget_type || 'stat');
        return {
            id: widgetDraft?.id || nextAssistantTempId(),
            dashboard_section_id: sectionId,
            title: widgetDraft?.title || `Widget ${fallbackPosition}`,
            widget_type: widgetType,
            position: widgetDraft?.position || fallbackPosition,
            enabled: widgetDraft?.enabled !== false,
            layout: {
                ...DEFAULT_LAYOUT,
                ...(widgetDraft?.layout || {}),
            },
            query_config: widgetDraft?.query_config || {},
            viz_config: widgetDraft?.viz_config || {},
        };
    }, [nextAssistantTempId]);

    const previewAssistantDashboardWidgets = useCallback(async (dashboard) => {
        if (!dashboard?.id) return;

        const widgets = (dashboard.dashboard_sections || []).flatMap((section) => section.dashboard_widgets || []);
        if (!widgets.length) return;

        setWidgetResults((prev) => widgets.reduce((acc, widget) => ({
            ...acc,
            [widget.id]: {
                ...(prev?.[widget.id] || {}),
                loading: true,
                refreshing: true,
                data: null,
                error: null,
            },
        }), { ...prev }));

        await Promise.all(widgets.map(async (widget) => {
            try {
                const response = await dashboardServices.previewWidget(dashboard.id, {
                    widget: buildWidgetPayload(widget),
                    dashboard_filters: {
                        filters: buildDashboardFiltersPayload(runtimeDashboardFilters),
                    },
                    dashboard_date_range: buildDateRangePayload(runtimeDateRange),
                });

                setWidgetResults((prev) => ({
                    ...prev,
                    [widget.id]: {
                        loading: false,
                        refreshing: false,
                        data: response,
                        error: null,
                    },
                }));
            } catch (_error) {
                setWidgetResults((prev) => ({
                    ...prev,
                    [widget.id]: {
                        ...(prev?.[widget.id] || {}),
                        loading: false,
                        refreshing: false,
                        error: true,
                    },
                }));
            }
        }));
    }, [runtimeDashboardFilters, runtimeDateRange]);

    // Un borrador de Xpit Assistant (draft_payload con mode, dashboard_id y dashboard_sections, ya expandidas por el
    // backend): con mode create arma un dashboard nuevo; con update suma o reemplaza secciones y widgets (por id) en el
    // seleccionado. Queda en modo edición y nada más se guarda hasta «Guardar cambios».
    const applyAssistantDashboardDraft = useCallback(async (message) => {
        if (!canEdit) return;

        const payload = message?.draft_payload || {};
        const sectionDrafts = Array.isArray(payload.dashboard_sections) ? payload.dashboard_sections : [];
        let baseDashboard = payload.mode === 'update' ? selectedDashboard : null;

        if (!baseDashboard?.id) {
            const createdDashboard = await dashboardServices.createDashboard({
                dashboard: {
                    name: payload.name || 'Dashboard IA',
                    description: payload.description || '',
                    filters_config: {
                        filters: [],
                        date_range: buildDateRangePayload(buildDefaultDateRangeState()),
                    },
                    settings: { auto_refresh: payload.settings?.auto_refresh || autoRefreshDefault },
                },
            });
            baseDashboard = normalizeDashboard(createdDashboard);
            if (!baseDashboard?.id) return;

            skipNextSelectedDashboardLoadRef.current = true;
            setSelectedDashboardId(`${baseDashboard.id}`);
            setDashboards((prev) => _.orderBy([
                ...(prev || []).filter((dashboard) => Number(dashboard.id) !== Number(baseDashboard.id)),
                baseDashboard,
            ], ['position', 'id'], ['asc', 'asc']));
        }

        const nextSections = [...(baseDashboard.dashboard_sections || [])];
        sectionDrafts.forEach((sectionDraft) => {
            const existingSectionIndex = sectionDraft?.id
                ? nextSections.findIndex((section) => Number(section.id) === Number(sectionDraft.id))
                : -1;
            const fallbackSection = existingSectionIndex >= 0 ? nextSections[existingSectionIndex] : null;
            const sectionId = fallbackSection?.id || nextAssistantTempId();
            const normalizedSection = {
                ...(fallbackSection || {}),
                id: sectionId,
                dashboard_id: baseDashboard.id,
                title: sectionDraft?.title || fallbackSection?.title || `Sección ${nextSections.length + 1}`,
                position: fallbackSection?.position || nextSections.length + 1,
                settings: fallbackSection?.settings || {},
                dashboard_widgets: [...(fallbackSection?.dashboard_widgets || [])],
            };

            (sectionDraft?.dashboard_widgets || []).forEach((widgetDraft, widgetIndex) => {
                const currentWidgetIndex = widgetDraft?.id
                    ? normalizedSection.dashboard_widgets.findIndex((widget) => Number(widget.id) === Number(widgetDraft.id))
                    : -1;
                const normalizedWidget = normalizeAssistantWidgetDraft(widgetDraft, sectionId, widgetIndex + 1);
                if (currentWidgetIndex >= 0) {
                    normalizedSection.dashboard_widgets[currentWidgetIndex] = {
                        ...normalizedSection.dashboard_widgets[currentWidgetIndex],
                        ...normalizedWidget,
                        id: normalizedSection.dashboard_widgets[currentWidgetIndex].id,
                    };
                } else {
                    normalizedSection.dashboard_widgets.push(normalizedWidget);
                }
            });

            normalizedSection.dashboard_widgets = _.orderBy(normalizedSection.dashboard_widgets, ['position', 'id'], ['asc', 'asc']);
            if (existingSectionIndex >= 0) {
                nextSections[existingSectionIndex] = normalizedSection;
            } else {
                nextSections.push(normalizedSection);
            }
        });

        const nextDashboard = normalizeDashboard({
            ...baseDashboard,
            name: payload.name || baseDashboard.name || 'Dashboard IA',
            description: payload.description ?? baseDashboard.description ?? '',
            dashboard_sections: _.orderBy(nextSections, ['position', 'id'], ['asc', 'asc']),
        });

        skipNextBulkWidgetQueryRef.current = true;
        setSelectedDashboard(nextDashboard);
        setDashboards((prev) => (prev || []).map((dashboard) => (
            Number(dashboard.id) === Number(nextDashboard.id)
                ? { ...dashboard, name: nextDashboard.name, description: nextDashboard.description }
                : dashboard
        )));
        setAssistantDraftDirty(true);
        setIsEditMode(true);
        previewAssistantDashboardWidgets(nextDashboard);
    }, [
        autoRefreshDefault,
        canEdit,
        nextAssistantTempId,
        normalizeAssistantWidgetDraft,
        previewAssistantDashboardWidgets,
        selectedDashboard,
    ]);

    // El borrador llega en el state de la navegación (desde el asistente, en cualquier página). Se aplica cuando la
    // lista ya cargó y, si edita uno, cuando ese dashboard es el seleccionado.
    useEffect(() => {
        const draft = location.state?.assistantDraft;
        if (!draft) return;

        pendingAssistantDraftRef.current = draft;
        setPendingAssistantDraftKey(location.key);
        navigate(location.pathname, { replace: true, state: {} });
    }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const draft = pendingAssistantDraftRef.current;
        if (!draft || !dashboardsLoadedRef.current || dashboardsLoading || dashboardLoading) return;

        const targetId = draft.draft_payload?.mode === 'update' ? draft.draft_payload?.dashboard_id : null;
        if (targetId && Number(selectedDashboard?.id) !== Number(targetId)) {
            if (`${selectedDashboardId}` !== `${targetId}` && dashboards.some((dashboard) => Number(dashboard.id) === Number(targetId))) {
                setAssistantDraftDirty(false);
                setSelectedDashboardId(`${targetId}`);
                return;
            }
            if (`${selectedDashboardId}` === `${targetId}`) return;
        }

        pendingAssistantDraftRef.current = null;
        applyAssistantDashboardDraft(draft).catch(() => {});
    }, [applyAssistantDashboardDraft, dashboardLoading, dashboards, dashboardsLoading, pendingAssistantDraftKey, selectedDashboard, selectedDashboardId]);

    const assistantPage = useMemo(() => ({
        context: {
            page: 'dashboards',
            current_dashboard: selectedDashboard ? {
                id: selectedDashboard.id,
                name: selectedDashboard.name,
                sections_count: selectedSections.length,
                widgets_count: selectedSections.reduce((acc, section) => acc + (section.dashboard_widgets || []).length, 0),
            } : null,
            is_edit_mode: isEditMode,
            active_filters_count: activeFiltersCount,
            date_range: runtimeDateRange,
        },
        quickActions: canEdit ? ['Crear dashboard de temperaturas', 'Mejorar este dashboard'] : [],
    }), [activeFiltersCount, canEdit, isEditMode, runtimeDateRange, selectedDashboard, selectedSections]);

    useAssistantPage(assistantPage);

    const handlePreviewWidget = async () => {
        if (!selectedDashboard || !widgetForm) return;

        setPreviewLoading(true);
        setPreviewResult(null);

        try {
            const response = await dashboardServices.previewWidget(selectedDashboard.id, {
                widget: buildWidgetPayload(widgetForm),
                dashboard_filters: {
                    filters: buildDashboardFiltersPayload(runtimeDashboardFilters),
                },
                dashboard_date_range: buildDateRangePayload(runtimeDateRange),
            });
            setPreviewResult({ ok: true, data: response });
        } catch (_error) {
            setPreviewResult({ ok: false, data: null });
        }

        setPreviewLoading(false);
    };

    const handleAddWidgetFilter = () => {
        if (!widgetForm) return;

        const defaultField = selectedDatasetFilterFields[0]?.key || 'device_name';
        setWidgetForm((prev) => ({
            ...prev,
            query_config: {
                ...prev.query_config,
                filters: [
                    ...(prev.query_config?.filters || []),
                    {
                        field: defaultField,
                        operator: 'contains',
                        value: '',
                    },
                ],
            },
        }));
    };

    const handleUpdateWidgetFilter = (index, patch) => {
        if (!widgetForm) return;

        setWidgetForm((prev) => ({
            ...prev,
            query_config: {
                ...prev.query_config,
                filters: _.map(prev.query_config?.filters || [], (filter, filterIndex) => (
                    filterIndex === index ? { ...filter, ...patch } : filter
                )),
            },
        }));
    };

    const handleRemoveWidgetFilter = (index) => {
        if (!widgetForm) return;

        setWidgetForm((prev) => ({
            ...prev,
            query_config: {
                ...prev.query_config,
                filters: (prev.query_config?.filters || []).filter((_filter, filterIndex) => filterIndex !== index),
            },
        }));
    };

    const persistSectionLayout = async (section, nextLayout) => {
        if (!selectedDashboard || !canEdit || !isEditMode || !section?.id || !Array.isArray(nextLayout) || busySectionId) return;

        if (assistantDraftDirty || !isPersistedRecordId(section.id)) {
            setSelectedDashboard((prev) => applySectionLayoutToDashboard(prev, section.id, nextLayout));
            setAssistantDraftDirty(true);
            return;
        }

        const sorted = _.sortBy(nextLayout, ['y', 'x']);
        const widgetIds = sorted.map((item) => Number(item.i));
        const positionById = sorted.reduce((acc, item, index) => {
            acc[Number(item.i)] = index + 1;
            return acc;
        }, {});

        setSelectedDashboard((prev) => applySectionLayoutToDashboard(prev, section.id, nextLayout));
        setBusySectionId(section.id);

        try {
            await dashboardServices.reorderWidgets(selectedDashboard.id, section.id, widgetIds);

            await Promise.all(
                nextLayout.map((layoutItem) => {
                    const widgetId = Number(layoutItem.i);
                    const currentWidget = (section.dashboard_widgets || []).find((widget) => Number(widget.id) === widgetId);
                    if (!currentWidget) return Promise.resolve();

                    return dashboardServices.updateWidget(selectedDashboard.id, widgetId, {
                        dashboard_widget: {
                            title: currentWidget.title,
                            widget_type: currentWidget.widget_type,
                            dashboard_section_id: currentWidget.dashboard_section_id || section.id,
                            position: positionById[widgetId] || currentWidget.position || 1,
                            enabled: currentWidget.enabled !== false,
                            layout: {
                                x: layoutItem.x,
                                y: layoutItem.y,
                                w: layoutItem.w,
                                h: layoutItem.h,
                            },
                            query_config: currentWidget.query_config || {},
                            viz_config: currentWidget.viz_config || {},
                        },
                    });
                })
            );
        } catch (_error) {
            await refreshSelectedDashboard();
        } finally {
            setBusySectionId(null);
        }
    };

    const openCustomDateRangeEditor = (anchorElement, rangeState = runtimeDateRange) => {
        const normalized = normalizeDateRangeState(rangeState);
        const now = new Date();
        const fallbackStart = normalizeStartOfDay(now);
        const fallbackEnd = normalizeEndOfDay(now);

        const fromDate = normalized.from ? normalizeStartOfDay(normalized.from) : fallbackStart;
        const toDate = normalized.to ? normalizeStartOfDay(normalized.to) : fallbackStart;
        setCustomDateRangeDraft([fromDate || fallbackStart, toDate || fallbackStart]);
        setRuntimeDateRange((prev) => ({
            ...prev,
            from: prev.from || fallbackStart.toISOString(),
            to: prev.to || fallbackEnd.toISOString(),
        }));
        setCustomDateRangeAnchorEl(anchorElement || null);
    };

    const closeCustomDateRangeEditor = () => {
        setCustomDateRangeAnchorEl(null);
    };

    const applyCustomDateRangeDays = (fromDay, toDay) => {
        if (!fromDay || !toDay) return;

        const fromStart = normalizeStartOfDay(fromDay);
        const toEnd = normalizeEndOfDay(toDay);
        if (!fromStart || !toEnd) return;

        setRuntimeDateRange({
            mode: 'custom',
            preset: '',
            from: fromStart.toISOString(),
            to: toEnd.toISOString(),
        });
        setCustomDateRangeDraft([normalizeStartOfDay(fromStart), normalizeStartOfDay(toEnd)]);
        setCustomDateRangeAnchorEl(null);
    };

    const applyCustomLastDays = (daysCount, previousDay = false) => {
        const end = normalizeEndOfDay(new Date());
        const start = normalizeStartOfDay(new Date());
        if (!start || !end) return;

        if (previousDay) {
            start.setDate(start.getDate() - 1);
            const previousEnd = normalizeEndOfDay(start);
            applyCustomDateRangeDays(start, previousEnd || start);
            return;
        }

        start.setDate(start.getDate() - Math.max(daysCount - 1, 0));
        applyCustomDateRangeDays(start, end);
    };

    const handleCustomCalendarChange = (nextValue) => {
        if (!nextValue) return;

        const pickedDay = normalizeStartOfDay(nextValue);
        if (!pickedDay) return;

        const [startDay, endDay] = customDateRangeDraft;
        if (!startDay || (startDay && endDay)) {
            setCustomDateRangeDraft([pickedDay, null]);
            return;
        }

        const normalizedStart = normalizeStartOfDay(startDay);
        if (!normalizedStart) {
            setCustomDateRangeDraft([pickedDay, null]);
            return;
        }

        const rangeStart = pickedDay.getTime() < normalizedStart.getTime() ? pickedDay : normalizedStart;
        const rangeEnd = pickedDay.getTime() < normalizedStart.getTime() ? normalizedStart : pickedDay;

        setCustomDateRangeDraft([rangeStart, rangeEnd]);
        applyCustomDateRangeDays(rangeStart, rangeEnd);
    };

    const handleDashboardRangeChange = (event) => {
        const selectedValue = event.target.value;

        if (selectedValue === DATE_RANGE_CUSTOM_KEY) {
            const now = new Date();
            const start = normalizeStartOfDay(now);
            const end = normalizeEndOfDay(now);
            const nextRange = updateDateRangeFromSelect(runtimeDateRange, selectedValue);
            const normalizedNextRange = {
                ...nextRange,
                from: nextRange.from || (start ? start.toISOString() : ''),
                to: nextRange.to || (end ? end.toISOString() : ''),
            };
            setRuntimeDateRange(normalizedNextRange);
            openCustomDateRangeEditor(event.currentTarget, normalizedNextRange);
            return;
        }

        setRuntimeDateRange((prev) => updateDateRangeFromSelect(prev, selectedValue));
        closeCustomDateRangeEditor();
    };

    // Los mismos campos sirven para el desplegable de escritorio y para el menú de filtros del teléfono.
    const renderDashboardFilterFields = () => (
        <Grid container spacing={1} sx={{ mt: 0.3 }}>
            <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                    <InputLabel>Local</InputLabel>
                    <Select
                        label="Local"
                        value={runtimeDashboardFilters.store_id || ''}
                        onChange={(event) => handleUpdateDashboardFixedFilter('store_id', event.target.value)}>
                        <MenuItem value=""><em>Todos</em></MenuItem>
                        {storeFilterOptions.map((option) => (
                            <MenuItem key={`dashboard-store-${option.value}`} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                    <InputLabel>Zona</InputLabel>
                    <Select
                        label="Zona"
                        value={runtimeDashboardFilters.zone || ''}
                        onChange={(event) => handleUpdateDashboardFixedFilter('zone', event.target.value)}>
                        <MenuItem value=""><em>Todas</em></MenuItem>
                        {zoneFilterOptions.map((option) => (
                            <MenuItem key={`dashboard-zone-${option.value}`} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                    <InputLabel>Tipo de dispositivo</InputLabel>
                    <Select
                        label="Tipo de dispositivo"
                        value={runtimeDashboardFilters.kind || ''}
                        onChange={(event) => handleUpdateDashboardFixedFilter('kind', event.target.value)}>
                        <MenuItem value=""><em>Todos</em></MenuItem>
                        {kindFilterOptions.map((option) => (
                            <MenuItem key={`dashboard-kind-${option.value}`} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                    <InputLabel>Dispositivo</InputLabel>
                    <Select
                        label="Dispositivo"
                        value={runtimeDashboardFilters.device_id || ''}
                        onChange={(event) => handleUpdateDashboardFixedFilter('device_id', event.target.value)}>
                        <MenuItem value=""><em>Todos</em></MenuItem>
                        {deviceFilterOptions.map((option) => (
                            <MenuItem key={`dashboard-device-${option.value}`} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
        </Grid>
    );

    const renderDashboardFilters = () => {
        return (
            <>
                <Paper
                    variant="outlined"
                    sx={{
                        p: { xs: 1.1, md: 1.2 },
                        borderRadius: 3,
                        borderColor: alpha('#5C149B', 0.16),
                        backgroundColor: alpha('#FFFFFF', 0.96),
                        backdropFilter: 'blur(8px)',
                        boxShadow: `0 10px 24px ${alpha('#0F172A', 0.08)}`,
                        position: 'sticky',
                        top: { xs: HEADER_HEIGHT + 8, xl: 10 },   // bajo 1200px la AppBar fija ocupa los primeros 68px
                        zIndex: 15,
                    }}>
                    {isMobileViewport ? (
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <FormControl
                                size="small"
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    '& .MuiOutlinedInput-root': { height: 40 },
                                }}>
                                <InputLabel>Rango</InputLabel>
                                <Select
                                    label="Rango"
                                    value={dateRangeSelectValue(runtimeDateRange)}
                                    onChange={handleDashboardRangeChange}>
                                    {QUICK_RANGE_OPTIONS.map((option) => (
                                        <MenuItem key={`dashboard-range-mobile-${option.key}`} value={option.key}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                    <MenuItem value={DATE_RANGE_CUSTOM_KEY}>Personalizado</MenuItem>
                                </Select>
                            </FormControl>
                            <Tooltip title="Más filtros">
                                <IconButton
                                    size="small"
                                    onClick={(event) => setMobileFiltersAnchor(event.currentTarget)}
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        flexShrink: 0,
                                        borderRadius: 1.4,
                                        border: `1px solid ${alpha('#5C149B', 0.4)}`,
                                        color: '#5C149B',
                                    }}>
                                    <Badge
                                        color="primary"
                                        overlap="circular"
                                        badgeContent={activeFiltersCount}
                                        sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 15, minWidth: 15 } }}>
                                        <FilterAltOutlinedIcon fontSize="small" />
                                    </Badge>
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    ) : (
                        <>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={0.9} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                <FilterAltOutlinedIcon fontSize="small" sx={{ color: '#5C149B' }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.1 }}>
                                    Filtros
                                </Typography>
                                <Chip
                                    size="small"
                                    label={activeFiltersCount > 0 ? `${activeFiltersCount} activos` : 'Sin filtros'}
                                    sx={{
                                        backgroundColor: activeFiltersCount > 0 ? alpha('#5C149B', 0.12) : alpha('#334155', 0.1),
                                        color: activeFiltersCount > 0 ? '#5C149B' : alpha('#0F172A', 0.7),
                                        fontWeight: 600,
                                    }}
                                />
                                <Chip
                                    size="small"
                                    label={activeRangeLabel}
                                    sx={{
                                        backgroundColor: alpha('#334155', 0.1),
                                        color: alpha('#0F172A', 0.78),
                                        fontWeight: 600,
                                        maxWidth: { xs: '100%', md: 300 },
                                        '& .MuiChip-label': {
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        },
                                    }}
                                />
                            </Stack>

                            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                <FormControl
                                    size="small"
                                    sx={{
                                        minWidth: { xs: 180, sm: 205 },
                                        maxWidth: { sm: 240 },
                                        '& .MuiOutlinedInput-root': {
                                            height: 40,
                                        },
                                    }}>
                                    <InputLabel>Rango</InputLabel>
                                    <Select
                                        label="Rango"
                                        value={dateRangeSelectValue(runtimeDateRange)}
                                        onChange={handleDashboardRangeChange}>
                                        {QUICK_RANGE_OPTIONS.map((option) => (
                                            <MenuItem key={`dashboard-range-${option.key}`} value={option.key}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                        <MenuItem value={DATE_RANGE_CUSTOM_KEY}>Personalizado</MenuItem>
                                    </Select>
                                </FormControl>
                                {runtimeDateRange.mode === 'custom' && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={(event) => openCustomDateRangeEditor(event.currentTarget)}
                                        sx={{
                                            minWidth: { xs: '100%', sm: 230 },
                                            maxWidth: { xs: '100%', sm: 260 },
                                            justifyContent: 'flex-start',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderColor: alpha('#5C149B', 0.35),
                                            color: alpha('#0F172A', 0.82),
                                            '&:hover': {
                                                borderColor: '#5C149B',
                                                backgroundColor: alpha('#5C149B', 0.04),
                                            },
                                        }}>
                                        <Box
                                            component="span"
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: '100%',
                                            }}>
                                            {customRangeCompactLabel || 'Seleccionar fechas'}
                                        </Box>
                                    </Button>
                                )}
                                <FormControl
                                    size="small"
                                    sx={{
                                        minWidth: { xs: 116, sm: 130 },
                                        '& .MuiOutlinedInput-root': {
                                            height: 40,
                                        },
                                    }}>
                                    <InputLabel>Refresh</InputLabel>
                                    <Select
                                        label="Refresh"
                                        value={activeAutoRefreshValue}
                                        onChange={(event) => setRuntimeAutoRefresh(event.target.value)}>
                                        {autoRefreshOptions.map((option) => (
                                            <MenuItem key={`dashboard-auto-refresh-${option.value}`} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {canEdit && (
                                    <Tooltip title="Guardar filtros">
                                        <span>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                disabled={savingFilters}
                                                onClick={saveDashboardFilters}
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 1.4,
                                                    border: `1px solid ${alpha('#5C149B', 0.4)}`,
                                                    backgroundColor: '#5C149B',
                                                    color: '#FFFFFF',
                                                    '&:hover': {
                                                        backgroundColor: '#4B1380',
                                                    },
                                                    '&.Mui-disabled': {
                                                        color: alpha('#FFFFFF', 0.75),
                                                        backgroundColor: alpha('#5C149B', 0.45),
                                                        borderColor: alpha('#5C149B', 0.25),
                                                    },
                                                }}>
                                                <SaveOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                                <Button
                                    variant="text"
                                    size="small"
                                    color="inherit"
                                    endIcon={filtersCollapsed ? <KeyboardArrowDownOutlinedIcon /> : <KeyboardArrowUpOutlinedIcon />}
                                    sx={{
                                        px: 1,
                                        color: alpha('#0F172A', 0.78),
                                        fontWeight: 600,
                                    }}
                                    onClick={() => setFiltersCollapsed((prev) => !prev)}>
                                    {filtersCollapsed ? 'Expandir' : 'Ocultar'}
                                </Button>
                            </Stack>
                        </Stack>

                        <Collapse in={!filtersCollapsed} timeout={170}>
                            {renderDashboardFilterFields()}
                        </Collapse>
                        </>
                    )}
                </Paper>

                {isMobileViewport && (
                    <Popover
                        open={Boolean(mobileFiltersAnchor)}
                        anchorEl={mobileFiltersAnchor}
                        onClose={() => setMobileFiltersAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{
                            paper: {
                                sx: {
                                    mt: 0.75,
                                    p: 1.5,
                                    width: 'min(360px, calc(100vw - 16px))',
                                    borderRadius: 3,
                                    overflowX: 'hidden',
                                },
                            },
                        }}>
                        <Stack spacing={1.25}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {activeRangeLabel}
                            </Typography>
                            {runtimeDateRange.mode === 'custom' && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={(event) => openCustomDateRangeEditor(event.currentTarget)}
                                    sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 600 }}>
                                    {customRangeCompactLabel || 'Seleccionar fechas'}
                                </Button>
                            )}
                            <FormControl fullWidth size="small">
                                <InputLabel>Refresh</InputLabel>
                                <Select
                                    label="Refresh"
                                    value={activeAutoRefreshValue}
                                    onChange={(event) => setRuntimeAutoRefresh(event.target.value)}>
                                    {autoRefreshOptions.map((option) => (
                                        <MenuItem key={`dashboard-auto-refresh-mobile-${option.value}`} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {renderDashboardFilterFields()}
                            {canEdit && (
                                <Button
                                    variant="contained"
                                    startIcon={<SaveOutlinedIcon />}
                                    disabled={savingFilters}
                                    onClick={saveDashboardFilters}
                                    sx={{ textTransform: 'none', fontWeight: 700 }}>
                                    Guardar filtros
                                </Button>
                            )}
                        </Stack>
                    </Popover>
                )}

                <Popover
                    open={customDateRangeEditorOpen}
                    anchorEl={customDateRangeAnchorEl}
                    onClose={closeCustomDateRangeEditor}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
                    <Stack spacing={1} sx={{ p: 1.25, width: { xs: 'min(92vw, 390px)', sm: 390 } }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Rango personalizado
                        </Typography>

                        <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                            {CUSTOM_RANGE_COMPACT_SHORTCUTS.map((option) => (
                                <Button
                                    key={`custom-range-shortcut-${option.key}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ minWidth: 0, px: 1.1, fontWeight: 600 }}
                                    onClick={() => applyCustomLastDays(option.days, option.previousDay === true)}>
                                    {option.label}
                                </Button>
                            ))}
                        </Stack>

                        <Typography variant="caption" color="text.secondary">
                            Selecciona día inicial y luego día final.
                        </Typography>

                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DateCalendar
                                value={customDateRangeDraft?.[1] || customDateRangeDraft?.[0] || null}
                                onChange={handleCustomCalendarChange}
                                disableFuture={false}
                            />
                        </LocalizationProvider>

                        <Chip
                            size="small"
                            label={`${formatCompactDate(customDateRangeDraft?.[0])} - ${formatCompactDate(customDateRangeDraft?.[1])}`}
                            sx={{
                                alignSelf: 'flex-start',
                                backgroundColor: alpha('#5C149B', 0.1),
                                color: '#5C149B',
                                fontWeight: 600,
                            }}
                        />

                        <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                            <Button size="small" color="inherit" onClick={closeCustomDateRangeEditor}>Cerrar</Button>
                        </Stack>
                    </Stack>
                </Popover>
            </>
        );
    };

    const renderSection = (section, sectionIndex) => {
        const widgets = section.dashboard_widgets || [];
        const sectionStats = widgets.reduce((acc, widget) => {
            const result = widgetResults?.[widget.id];
            const hasData = !_.isNil(result?.data);

            if (hasData) acc.withData += 1;
            if (result?.error && !hasData) acc.withError += 1;
            return acc;
        }, { withData: 0, withError: 0 });

        return (
            <Paper
                key={`section-${section.id}`}
                variant="outlined"
                sx={{
                    p: { xs: 0.75, md: 1.85 },
                    borderRadius: 3,
                    borderColor: alpha('#5C149B', 0.14),
                    backgroundColor: '#FFFFFF',
                    boxShadow: `0 12px 34px ${alpha('#0F172A', 0.08)}`,
                }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1.25}>
                    <Stack direction="row" spacing={0.9} alignItems="center">
                        <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.15 }}>
                            {section.title}
                        </Typography>
                        <Chip
                            size="small"
                            label={`${widgets.length} widget${widgets.length === 1 ? '' : 's'}`}
                            sx={{
                                backgroundColor: alpha('#5C149B', 0.12),
                                color: '#5C149B',
                                fontWeight: 600,
                            }}
                        />
                        {sectionStats.withError > 0 && (
                            <Chip
                                size="small"
                                label={`${sectionStats.withError} con error`}
                                sx={{
                                    backgroundColor: alpha('#DC2626', 0.12),
                                    color: '#B91C1C',
                                    fontWeight: 600,
                                }}
                            />
                        )}
                    </Stack>

                    <Stack direction="row" spacing={0.75}>
                        {canEdit && isEditMode && (
                            <>
                                <Tooltip title="Agregar widget">
                                    <IconButton size="small" color="primary" onClick={() => openWidgetDialog(section.id)}>
                                        <AddCircleOutlineOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Renombrar sección">
                                    <IconButton size="small" onClick={() => handleRenameSection(section)}>
                                        <EditOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Mover arriba">
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={sectionIndex === 0}
                                            onClick={() => handleMoveSection(section, 'up')}>
                                            <ArrowUpwardOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Mover abajo">
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={sectionIndex === selectedSections.length - 1}
                                            onClick={() => handleMoveSection(section, 'down')}>
                                            <ArrowDownwardOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Eliminar sección">
                                    <IconButton size="small" color="error" onClick={() => handleDeleteSection(section)}>
                                        <DeleteOutlineOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </Stack>
                </Stack>

                {busySectionId === section.id && (
                    <Box sx={{ mt: 1.5, mb: 1 }}>
                        <Alert severity="info">Guardando nuevo layout de widgets...</Alert>
                    </Box>
                )}

                {!widgets.length ? (
                    <Typography sx={{ mt: 2 }} color="text.secondary">Esta sección no tiene widgets.</Typography>
                ) : (
                    <Box sx={{ mt: 1.75 }}>
                        <GridLayout
                            cols={12}
                            rowHeight={28}
                            margin={isMobileViewport ? [8, 8] : [12, 12]}
                            containerPadding={[0, 0]}
                            isDraggable={canEdit && isEditMode}
                            isResizable={canEdit && isEditMode}
                            onDragStop={(layout) => persistSectionLayout(section, layout)}
                            onResizeStop={(layout) => persistSectionLayout(section, layout)}>
                            {widgets.map((widget, widgetIndex) => {
                                const singleWidget = widgets.length === 1;
                                const defaultX = (widgetIndex % 2) * 6;
                                const widgetVisual = getWidgetVisualMeta(widget.widget_type);
                                const WidgetIcon = widgetVisual.Icon;
                                const widgetTypeLabel = WIDGET_LABELS[canonicalWidgetType(widget.widget_type)] || widget.widget_type;
                                const widgetResult = widgetResults?.[widget.id] || {};
                                const widgetHasData = !_.isNil(widgetResult?.data);
                                const widgetHasError = Boolean(widgetResult?.error && !widgetHasData);
                                const widgetIsOk = widgetHasData && !widgetHasError;
                                const widgetStatusColor = widgetIsOk ? '#34D399' : '#CBD5E1';
                                const mobileDataGrid = {
                                    ...DEFAULT_LAYOUT,
                                    ...widget.layout,
                                    i: `${widget.id}`,
                                    x: 0,
                                    w: 12,
                                    minW: 12,
                                    maxW: 12,
                                    y: widgetIndex * DEFAULT_LAYOUT.h,
                                };
                                const dataGrid = {
                                    ...DEFAULT_LAYOUT,
                                    ...widget.layout,
                                    i: `${widget.id}`,
                                    x: singleWidget ? 0 : (_.isNumber(widget.layout?.x) ? widget.layout.x : defaultX),
                                    w: singleWidget ? 12 : (_.isNumber(widget.layout?.w) ? widget.layout.w : DEFAULT_LAYOUT.w),
                                    y: _.isNumber(widget.layout?.y) ? widget.layout.y : Math.floor(widgetIndex / 2) * 9,
                                };
                                const resolvedDataGrid = isMobileViewport ? mobileDataGrid : dataGrid;

                                return (
                                    <div key={`${widget.id}`} data-grid={resolvedDataGrid}>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: { xs: 0.9, md: 1.3 },
                                                pt: { xs: 1, md: 1.4 },
                                                height: '100%',
                                                borderRadius: 2.5,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                position: 'relative',
                                                overflow: 'hidden',
                                                borderColor: alpha(widgetVisual.accent, 0.24),
                                                backgroundColor: '#FFFFFF',
                                                boxShadow: `0 10px 22px ${alpha('#0F172A', 0.07)}`,
                                                transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                                                '&:hover': {
                                                    transform: 'translateY(-1px)',
                                                    borderColor: alpha(widgetVisual.accent, 0.42),
                                                    boxShadow: `0 18px 30px ${alpha(widgetVisual.accent, 0.18)}`,
                                                },
                                            }}>
                                            {widgetResults?.[widget.id]?.refreshing && (
                                                <LinearProgress
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        height: 3,
                                                        borderRadius: 0,
                                                        backgroundColor: alpha(widgetVisual.accent, 0.11),
                                                        '& .MuiLinearProgress-bar': {
                                                            backgroundColor: alpha(widgetVisual.accent, 0.4),
                                                        },
                                                    }}
                                                />
                                            )}
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                                    <Box
                                                        sx={{
                                                            width: 30,
                                                            height: 30,
                                                            borderRadius: 1.6,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            backgroundColor: widgetVisual.tint,
                                                            color: widgetVisual.accent,
                                                            flexShrink: 0,
                                                        }}>
                                                        <WidgetIcon sx={{ fontSize: 18 }} />
                                                    </Box>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{
                                                                fontWeight: 700,
                                                                letterSpacing: 0.1,
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                            }}>
                                                            {widget.title}
                                                        </Typography>
                                                        <Stack direction="row" spacing={0.7} alignItems="center">
                                                            <Box
                                                                sx={{
                                                                    width: 8,
                                                                    height: 8,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: widgetStatusColor,
                                                                    flexShrink: 0,
                                                                    alignSelf: 'center',
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: alpha('#0F172A', 0.62),
                                                                    fontWeight: 600,
                                                                    lineHeight: 1,
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                }}>
                                                                {widgetTypeLabel}
                                                            </Typography>
                                                        </Stack>
                                                    </Box>
                                                </Stack>

                                                {canEdit && isEditMode && (
                                                    <Stack direction="row" spacing={0.3}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => openWidgetDialog(section.id, widget)}
                                                            sx={{
                                                                color: alpha('#0F172A', 0.74),
                                                                '&:hover': {
                                                                    backgroundColor: alpha(widgetVisual.accent, 0.1),
                                                                    color: widgetVisual.accent,
                                                                },
                                                            }}>
                                                            <EditOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleWidgetClone(section, widget)}
                                                            sx={{
                                                                color: alpha('#0F172A', 0.74),
                                                                '&:hover': {
                                                                    backgroundColor: alpha(widgetVisual.accent, 0.1),
                                                                    color: widgetVisual.accent,
                                                                },
                                                            }}>
                                                            <ContentCopyOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleWidgetDelete(widget)}
                                                            sx={{
                                                                '&:hover': {
                                                                    backgroundColor: alpha('#DC2626', 0.14),
                                                                },
                                                            }}>
                                                            <DeleteOutlineOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </Stack>
                                                )}
                                            </Stack>

                                            <Box
                                                sx={{
                                                    mt: 1.1,
                                                    flex: 1,
                                                    minHeight: 0,
                                                    overflow: 'hidden',
                                                    borderRadius: 1.7,
                                                    backgroundColor: alpha('#FFFFFF', 0.62),
                                                    p: 0.25,
                                                }}>
                                                <WidgetResultView
                                                    widget={widget}
                                                    result={widgetResults?.[widget.id]?.data}
                                                    loading={widgetResults?.[widget.id]?.loading}
                                                    error={widgetResults?.[widget.id]?.error}
                                                    mapApiLoaded={mapApiLoaded}
                                                    mapEnabled={mapEnabled}
                                                    dashboardDateRangeState={runtimeDateRange}
                                                    fillHeight
                                                />
                                            </Box>
                                        </Paper>
                                    </div>
                                );
                            })}
                        </GridLayout>
                    </Box>
                )}
            </Paper>
        );
    };

    return (
        <Box sx={{ width: '100%', minWidth: 0 }}>
                <Stack spacing={1.5}>
                {dashboardLoading ? (
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            textAlign: 'center',
                            borderColor: alpha('#5C149B', 0.16),
                            background: `linear-gradient(180deg, ${alpha('#5C149B', 0.06)} 0%, rgba(255,255,255,0.98) 100%)`,
                        }}>
                        <CircularProgress size={26} />
                    </Paper>
                ) : !selectedDashboard ? (
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            borderColor: alpha('#5C149B', 0.16),
                            background: `linear-gradient(180deg, ${alpha('#EEF2FF', 0.65)} 0%, rgba(255,255,255,0.97) 100%)`,
                        }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                            <Typography color="text.secondary">Selecciona o crea un dashboard para comenzar.</Typography>
                            {canEdit && (
                                <Tooltip title="Nuevo dashboard">
                                    <IconButton size="small" color="primary" onClick={handleCreateDashboard}>
                                        <AddCircleOutlineOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Stack>
                    </Paper>
                ) : (
                    <Stack spacing={1.5}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: { xs: 1, md: 2 },
                                borderRadius: 3,
                                borderColor: alpha('#5C149B', 0.16),
                                background: `linear-gradient(120deg, rgba(255,255,255,0.98) 56%, ${alpha('#5C149B', 0.09)} 100%)`,
                                boxShadow: `0 12px 30px ${alpha('#0F172A', 0.08)}`,
                            }}>
                            <Stack direction="row" justifyContent="space-between" spacing={1.2} alignItems="center">
                                <Stack spacing={0.9} sx={{ minWidth: 0, flex: 1 }}>
                                    <Stack direction="row" spacing={0.65} alignItems="center" sx={{ minWidth: 0 }}>
                                        <Box
                                            sx={{
                                                width: 30,
                                                height: 30,
                                                borderRadius: 1.35,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: '#5C149B',
                                                backgroundColor: alpha('#5C149B', 0.12),
                                                border: `1px solid ${alpha('#5C149B', 0.2)}`,
                                            }}>
                                            <DashboardOutlinedIcon sx={{ fontSize: 18 }} />
                                        </Box>
                                        <Typography variant="h5" noWrap sx={{ minWidth: 0, fontWeight: 700, letterSpacing: 0.2 }}>
                                            {selectedDashboard.name}
                                        </Typography>
                                        <Tooltip title="Cambiar dashboard">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={openDashboardPicker}
                                                    disabled={dashboardsLoading || dashboards.length <= 1}
                                                    sx={{ color: alpha('#0F172A', 0.72) }}>
                                                    {dashboardPickerOpen ? <KeyboardArrowUpOutlinedIcon /> : <KeyboardArrowDownOutlinedIcon />}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        {dashboardsLoading && <CircularProgress size={16} />}
                                    </Stack>

                                    {widgetsErrorCount > 0 && (
                                        <Chip
                                            size="small"
                                            label={`${widgetsErrorCount} con error`}
                                            sx={{
                                                backgroundColor: alpha('#DC2626', 0.12),
                                                color: '#B91C1C',
                                                fontWeight: 700,
                                                alignSelf: 'flex-start',
                                            }}
                                        />
                                    )}
                                </Stack>

                                <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                                    {canEdit && !isMobileViewport && isEditMode && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="warning"
                                            disabled={savingAssistantDraft}
                                            onClick={handleFinishEditMode}
                                            sx={{ textTransform: 'none', fontWeight: 600 }}>
                                            {assistantDraftDirty
                                                ? (savingAssistantDraft ? 'Guardando...' : 'Guardar cambios')
                                                : 'Terminar edición'}
                                        </Button>
                                    )}
                                    {canEdit && !isMobileViewport && !isEditMode && (
                                        <Tooltip title="Editar dashboard">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => setIsEditMode(true)}
                                                sx={{
                                                    border: `1px solid ${alpha('#5C149B', 0.45)}`,
                                                    backgroundColor: alpha('#5C149B', 0.06),
                                                    '&:hover': {
                                                        backgroundColor: alpha('#5C149B', 0.12),
                                                    },
                                                }}>
                                                <EditOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {canEdit && !isMobileViewport && (
                                        <Tooltip title="Nuevo dashboard">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={handleCreateDashboard}
                                                sx={{
                                                    border: `1px solid ${alpha('#5C149B', 0.45)}`,
                                                    backgroundColor: alpha('#5C149B', 0.06),
                                                    '&:hover': {
                                                        backgroundColor: alpha('#5C149B', 0.12),
                                                    },
                                                }}>
                                                <AddCircleOutlineOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {canEdit && (
                                        <Tooltip title="Acciones del dashboard">
                                            <IconButton size="small" onClick={openDashboardActionsMenu}>
                                                <MoreVertOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Stack>
                            </Stack>
                        </Paper>

                        <Popover
                            open={dashboardPickerOpen}
                            anchorEl={dashboardPickerAnchorEl}
                            onClose={closeDashboardPicker}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
                            <Stack sx={{ py: 0.4, minWidth: 260, maxHeight: 320, overflowY: 'auto' }}>
                                {!dashboards.length ? (
                                    <Typography sx={{ px: 1.5, py: 1 }} color="text.secondary">No hay dashboards</Typography>
                                ) : (
                                    dashboards.map((dashboard) => {
                                        const isCurrent = `${dashboard.id}` === `${selectedDashboard.id}`;
                                        return (
                                            <MenuItem
                                                key={`dashboard-picker-item-${dashboard.id}`}
                                                selected={isCurrent}
                                                onClick={() => handleSelectDashboardFromPicker(dashboard.id)}
                                                sx={isCurrent ? { fontWeight: 700 } : undefined}>
                                                <Stack sx={{ minWidth: 0 }}>
                                                    {dashboard.name}
                                                    {dashboard.created_via && (
                                                        <Typography variant="caption" color="text.secondary">Creado vía {dashboard.created_via}</Typography>
                                                    )}
                                                </Stack>
                                            </MenuItem>
                                        );
                                    })
                                )}
                            </Stack>
                        </Popover>

                        <Popover
                            open={dashboardActionsOpen}
                            anchorEl={dashboardActionsAnchorEl}
                            onClose={closeDashboardActionsMenu}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                            <Stack sx={{ py: 0.4, minWidth: 190 }}>
                                {isMobileViewport && !isEditMode && <MenuItem onClick={handleToggleEditModeFromMenu}>Editar</MenuItem>}
                                {isMobileViewport && isEditMode && (
                                    <MenuItem onClick={handleFinishEditModeFromMenu} disabled={savingAssistantDraft}>
                                        {assistantDraftDirty ? (savingAssistantDraft ? 'Guardando...' : 'Guardar cambios') : 'Terminar edición'}
                                    </MenuItem>
                                )}
                                {isMobileViewport && <MenuItem onClick={handleCreateDashboardFromMenu}>Nuevo dashboard</MenuItem>}
                                <MenuItem onClick={handleRenameDashboardFromMenu}>Renombrar</MenuItem>
                                <MenuItem onClick={handleDeleteDashboardFromMenu} sx={{ color: 'error.main' }}>Eliminar</MenuItem>
                            </Stack>
                        </Popover>

                        {renderDashboardFilters()}

                        {canEdit && isEditMode && (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 1.5,
                                    borderRadius: 3,
                                    borderColor: alpha('#5C149B', 0.16),
                                    backgroundColor: '#FFFFFF',
                                }}>
                                <Button startIcon={<AddCircleOutlineOutlinedIcon />} onClick={handleCreateSection}>
                                    Agregar sección
                                </Button>
                            </Paper>
                        )}

                        {!selectedSections.length ? (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 3,
                                    borderRadius: 3,
                                    borderColor: alpha('#5C149B', 0.16),
                                    background: `linear-gradient(180deg, ${alpha('#EEF2FF', 0.7)} 0%, rgba(255,255,255,0.98) 100%)`,
                                }}>
                                <Typography color="text.secondary">Este dashboard no tiene secciones todavía.</Typography>
                            </Paper>
                        ) : (
                            <Stack spacing={1.5}>
                                {selectedSections.map((section, index) => renderSection(section, index))}
                            </Stack>
                        )}
                    </Stack>
                )}
            </Stack>

            <Dialog open={createDashboardDialogOpen} onClose={closeCreateDashboardDialog} fullWidth maxWidth="xs">
                <DialogTitle>{createDashboardDialogMode === 'rename' ? 'Renombrar dashboard' : 'Nuevo dashboard'}</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label="Nombre"
                        value={createDashboardName}
                        disabled={creatingDashboard}
                        onChange={(event) => setCreateDashboardName(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                submitCreateDashboard();
                            }
                        }}
                        sx={{ mt: 0.5 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeCreateDashboardDialog} disabled={creatingDashboard}>Cancelar</Button>
                    <Button
                        variant="contained"
                        onClick={submitCreateDashboard}
                        disabled={creatingDashboard || !`${createDashboardName || ''}`.trim()}>
                        {createDashboardDialogMode === 'rename' ? 'Guardar' : 'Crear'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={widgetDialogOpen} onClose={closeWidgetDialog} fullWidth maxWidth="lg">
                <DialogTitle>
                    {widgetForm?.id ? 'Editar widget' : 'Nuevo widget'}
                </DialogTitle>

                <DialogContent dividers>
                    {!widgetForm ? (
                        <Box sx={{ py: 3, textAlign: 'center' }}>
                            <CircularProgress size={22} />
                        </Box>
                    ) : (
                        <Stack spacing={1.5}>
                            <Grid container spacing={1.25}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Título"
                                        value={widgetForm.title || ''}
                                        onChange={(event) => setWidgetForm((prev) => ({ ...prev, title: event.target.value }))}
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Tipo</InputLabel>
                                        <Select
                                            label="Tipo"
                                            value={canonicalWidgetType(widgetForm.widget_type || 'timeseries')}
                                            onChange={(event) => handleWidgetTypeChange(event.target.value)}>
                                            {widgetTypeOptions.map((type) => (
                                                <MenuItem key={`widget-type-${type}`} value={type}>{WIDGET_LABELS[type] || type}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Dataset</InputLabel>
                                        <Select
                                            label="Dataset"
                                            value={widgetForm.query_config?.dataset || ''}
                                            onChange={(event) => handleWidgetDatasetChange(event.target.value)}>
                                            {Object.keys(datasetsMap).map((datasetKey) => (
                                                <MenuItem key={`dataset-${datasetKey}`} value={datasetKey}>
                                                    {datasetsMap?.[datasetKey]?.label || datasetKey}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Grid container spacing={1.25}>
                                {activeWidgetFormRules.x && (
                                    <Grid item xs={12} md={3}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Eje X</InputLabel>
                                            <Select
                                                label="Eje X"
                                                value={widgetForm.query_config?.x || ''}
                                                onChange={(event) => setWidgetForm((prev) => ({
                                                    ...prev,
                                                    query_config: {
                                                        ...prev.query_config,
                                                        x: event.target.value,
                                                    },
                                                }))}>
                                                {selectedDatasetFields.map((field) => (
                                                    <MenuItem key={`x-field-${field.key}`} value={field.key}>
                                                        {selectedDatasetFieldLabelMap[field.key] || field.key}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}

                                {activeWidgetFormRules.y && (
                                    <Grid item xs={12} md={activeWidgetFormRules.x ? 4 : 6}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>{isStateTimelineWidget ? 'Campo estado' : 'Métricas Y'}</InputLabel>
                                            <Select
                                                multiple={!isStateTimelineWidget}
                                                label={isStateTimelineWidget ? 'Campo estado' : 'Métricas Y'}
                                                value={isStateTimelineWidget
                                                    ? (widgetForm.query_config?.y_metrics?.[0]?.field || '')
                                                    : (widgetForm.query_config?.y_metrics || []).map((metric) => metric.field)}
                                                onChange={(event) => {
                                                    const currentMap = _.keyBy(widgetForm.query_config?.y_metrics || [], 'field');
                                                    const resolveMetricLabel = (fieldKey) => (
                                                        currentMap[fieldKey]?.label
                                                        || selectedDatasetMetricLabelMap[fieldKey]
                                                        || getFieldDisplayLabel(fieldKey)
                                                        || fieldKey
                                                    );

                                                    if (isStateTimelineWidget) {
                                                        const field = `${event.target.value || ''}`.trim();
                                                        const nextMetrics = field ? [{ field, label: resolveMetricLabel(field) }] : [];
                                                        setWidgetForm((prev) => ({
                                                            ...prev,
                                                            query_config: {
                                                                ...prev.query_config,
                                                                y_metrics: nextMetrics,
                                                            },
                                                        }));
                                                        return;
                                                    }

                                                    const selected = Array.isArray(event.target.value) ? event.target.value : [];
                                                    const nextMetrics = selected.map((field) => ({
                                                        field,
                                                        label: resolveMetricLabel(field),
                                                    }));

                                                    setWidgetForm((prev) => ({
                                                        ...prev,
                                                        query_config: {
                                                            ...prev.query_config,
                                                            y_metrics: nextMetrics,
                                                        },
                                                    }));
                                                }}
                                                renderValue={(value) => {
                                                    if (isStateTimelineWidget) {
                                                        const key = `${value || ''}`.trim();
                                                        return widgetMetricLabelMap[key]?.label
                                                            || selectedDatasetMetricLabelMap[key]
                                                            || getFieldDisplayLabel(key)
                                                            || key;
                                                    }

                                                    return (Array.isArray(value) ? value : [])
                                                        .map((fieldKey) => (
                                                            widgetMetricLabelMap[fieldKey]?.label
                                                            || selectedDatasetMetricLabelMap[fieldKey]
                                                            || getFieldDisplayLabel(fieldKey)
                                                            || fieldKey
                                                        ))
                                                        .join(', ');
                                                }}>
                                                {selectedDatasetMetricFields.map((field) => (
                                                    <MenuItem key={`y-field-${field.key}`} value={field.key}>
                                                        {selectedDatasetMetricLabelMap[field.key] || field.key}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}

                                {activeWidgetFormRules.y && (widgetForm.query_config?.y_metrics || []).length > 0 && (
                                    <Grid item xs={12}>
                                        <Grid container spacing={1.25}>
                                            {(widgetForm.query_config?.y_metrics || []).map((metric, metricIndex) => (
                                                <Grid item xs={12} md={6} key={`metric-label-${metric.field}-${metricIndex}`}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label={`Etiqueta en gráfico: ${selectedDatasetMetricLabelMap[metric.field] || getFieldDisplayLabel(metric.field)}`}
                                                        value={metric.label || ''}
                                                        onChange={(event) => setWidgetForm((prev) => ({
                                                            ...prev,
                                                            query_config: {
                                                                ...prev.query_config,
                                                                y_metrics: (prev.query_config?.y_metrics || []).map((entry, index) => (
                                                                    index === metricIndex
                                                                        ? { ...entry, label: event.target.value }
                                                                        : entry
                                                                )),
                                                            },
                                                        }))}
                                                        helperText="Se mostrará en leyenda y tooltip."
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Grid>
                                )}

                                {activeWidgetFormRules.aggregation && (
                                    <Grid item xs={12} md={3}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Agregación</InputLabel>
                                            <Select
                                                label="Agregación"
                                                value={widgetForm.query_config?.aggregation || ''}
                                                onChange={(event) => setWidgetForm((prev) => ({
                                                    ...prev,
                                                    query_config: {
                                                        ...prev.query_config,
                                                        aggregation: event.target.value,
                                                    },
                                                }))}>
                                                {availableAggregationOptions.map((aggregation) => (
                                                    <MenuItem key={`agg-${aggregation}`} value={aggregation}>
                                                        {AGGREGATION_LABELS[aggregation] || aggregation}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {counterAggregations.includes(selectedAggregation) && (
                                                <FormHelperText>
                                                    Para valores acumulados: cuánto aumentó en cada intervalo.
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>
                                )}

                                {activeWidgetFormRules.groupBy && (
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Group by</InputLabel>
                                            <Select
                                                multiple
                                                label="Group by"
                                                value={widgetForm.query_config?.group_by || []}
                                                onChange={(event) => setWidgetForm((prev) => ({
                                                    ...prev,
                                                    query_config: {
                                                        ...prev.query_config,
                                                        group_by: event.target.value,
                                                    },
                                                }))}
                                                renderValue={(value) => value.map((fieldKey) => selectedDatasetFieldLabelMap[fieldKey] || fieldKey).join(', ')}>
                                                {selectedDatasetGroupFields.map((field) => (
                                                    <MenuItem key={`group-by-${field.key}`} value={field.key}>
                                                        {selectedDatasetFieldLabelMap[field.key] || field.key}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}

                                {activeWidgetFormRules.labelField && (
                                    <Grid item xs={12} md={3}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Campo label</InputLabel>
                                            <Select
                                                label="Campo label"
                                                value={widgetForm.query_config?.label_field || ''}
                                                onChange={(event) => setWidgetForm((prev) => ({
                                                    ...prev,
                                                    query_config: {
                                                        ...prev.query_config,
                                                        label_field: event.target.value,
                                                    },
                                                }))}>
                                                {selectedDatasetLabelFields.map((field) => (
                                                    <MenuItem key={`label-field-${field.key}`} value={field.key}>
                                                        {selectedDatasetFieldLabelMap[field.key] || field.key}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}
                            </Grid>

                            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                <Grid container spacing={1.25}>
                                    <Grid item xs={12} md={3}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Fuente de rango</InputLabel>
                                            <Select
                                                label="Fuente de rango"
                                                value={widgetDateRangeSource}
                                                onChange={(event) => setWidgetForm((prev) => ({
                                                    ...prev,
                                                    query_config: {
                                                        ...prev.query_config,
                                                        date_range_source: event.target.value,
                                                    },
                                                }))}>
                                                <MenuItem value="dashboard">Dashboard</MenuItem>
                                                <MenuItem value="fixed">Fijo del widget</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    {widgetDateRangeSource === 'fixed' && (
                                        <Grid item xs={12} md={4}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Rango widget</InputLabel>
                                                <Select
                                                    label="Rango widget"
                                                    value={dateRangeSelectValue(widgetForm.query_config?.date_range)}
                                                    onChange={(event) => setWidgetForm((prev) => ({
                                                        ...prev,
                                                        query_config: {
                                                            ...prev.query_config,
                                                            date_range: updateDateRangeFromSelect(prev.query_config?.date_range, event.target.value),
                                                        },
                                                    }))}>
                                                    {QUICK_RANGE_OPTIONS.map((option) => (
                                                        <MenuItem key={`widget-range-${option.key}`} value={option.key}>
                                                            {option.label}
                                                        </MenuItem>
                                                    ))}
                                                    <MenuItem value={DATE_RANGE_CUSTOM_KEY}>Personalizado</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    )}

                                    <Grid item xs={12} md={widgetDateRangeSource === 'fixed' ? 5 : 9}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Bucket (auto)"
                                            value={effectiveWidgetAutoBucket}
                                            InputProps={{ readOnly: true }}
                                            helperText={widgetDateRangeSource === 'dashboard'
                                                ? `Tomado del dashboard: ${describeDateRangeState(runtimeDateRange)}`
                                                : `Rango del widget: ${describeDateRangeState(widgetForm.query_config?.date_range)}`}
                                        />
                                    </Grid>

                                    {widgetDateRangeSource === 'fixed' && widgetDateRangeState.mode === 'custom' && (
                                        <>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Desde (widget)"
                                                    type="datetime-local"
                                                    value={widgetDateRangeState.from}
                                                    onChange={(event) => setWidgetForm((prev) => ({
                                                        ...prev,
                                                        query_config: {
                                                            ...prev.query_config,
                                                            date_range: {
                                                                ...normalizeDateRangeState(prev.query_config?.date_range),
                                                                mode: 'custom',
                                                                from: event.target.value,
                                                            },
                                                        },
                                                    }))}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Hasta (widget)"
                                                    type="datetime-local"
                                                    value={widgetDateRangeState.to}
                                                    onChange={(event) => setWidgetForm((prev) => ({
                                                        ...prev,
                                                        query_config: {
                                                            ...prev.query_config,
                                                            date_range: {
                                                                ...normalizeDateRangeState(prev.query_config?.date_range),
                                                                mode: 'custom',
                                                                to: event.target.value,
                                                            },
                                                        },
                                                    }))}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="subtitle2">Filtros del widget (AND)</Typography>
                                    <Button size="small" onClick={handleAddWidgetFilter}>Agregar filtro</Button>
                                </Stack>

                                <Stack spacing={1}>
                                    {(widgetForm.query_config?.filters || []).map((filter, index) => (
                                        <Grid container spacing={1} key={`widget-filter-${index}`} alignItems="center">
                                            <Grid item xs={12} md={4}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Campo</InputLabel>
                                                    <Select
                                                        label="Campo"
                                                        value={filter.field || ''}
                                                        onChange={(event) => handleUpdateWidgetFilter(index, { field: event.target.value })}>
                                                        {selectedDatasetFilterFields.map((field) => (
                                                            <MenuItem key={`widget-filter-field-${field.key}`} value={field.key}>
                                                                {selectedDatasetFieldLabelMap[field.key] || field.key}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} md={3}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Operador</InputLabel>
                                                    <Select
                                                        label="Operador"
                                                        value={filter.operator || 'contains'}
                                                        onChange={(event) => handleUpdateWidgetFilter(index, { operator: event.target.value })}>
                                                        {operatorOptions.map((operator) => (
                                                            <MenuItem key={`widget-filter-operator-${operator}`} value={operator}>
                                                                {OPERATOR_LABELS[operator] || operator}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} md={4}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Valor"
                                                    value={filter.value ?? ''}
                                                    onChange={(event) => handleUpdateWidgetFilter(index, { value: event.target.value })}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={1}>
                                                <IconButton color="error" onClick={() => handleRemoveWidgetFilter(index)}>
                                                    <RemoveCircleOutlineOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    ))}
                                </Stack>
                            </Paper>

                            {['timeseries', 'bar', 'stat'].includes(activeWidgetType) && (
                                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Visualización
                                    </Typography>

                                    <Grid container spacing={1.25}>
                                        <Grid item xs={12} md={activeWidgetType === 'stat' ? 4 : 6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Unidad de medida"
                                                placeholder="Ej: personas, °C, kWh"
                                                value={widgetForm?.viz_config?.unit || ''}
                                                onChange={(event) => setWidgetForm((prev) => ({
                                                    ...prev,
                                                    viz_config: {
                                                        ...(prev.viz_config || {}),
                                                        unit: event.target.value,
                                                    },
                                                }))}
                                                helperText={activeWidgetType === 'stat'
                                                    ? 'Se mostrará junto al KPI, en tooltip y eje Y cuando aplique.'
                                                    : 'Se mostrará en eje Y y tooltip.'}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={activeWidgetType === 'stat' ? 2 : 6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Decimales"
                                                type="number"
                                                inputProps={{ min: 0, max: MAX_METRIC_DECIMALS, step: 1 }}
                                                value={widgetMetricDecimals}
                                                onChange={(event) => setWidgetForm((prev) => ({
                                                    ...prev,
                                                    viz_config: {
                                                        ...(prev.viz_config || {}),
                                                        decimals: resolveMetricDecimals(event.target.value),
                                                    },
                                                }))}
                                                helperText="Por defecto: 1 decimal para no enteros."
                                            />
                                        </Grid>

                                        {['timeseries', 'bar'].includes(activeWidgetType) && (
                                            <>
                                                <Grid item xs={12} md={3}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label="Y min (opcional)"
                                                        type="number"
                                                        value={widgetYAxisMin ?? ''}
                                                        onChange={(event) => setWidgetForm((prev) => ({
                                                            ...prev,
                                                            viz_config: {
                                                                ...(prev.viz_config || {}),
                                                                y_axis_min: event.target.value,
                                                            },
                                                        }))}
                                                        helperText="Vacío = automático"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={3}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label="Y max (opcional)"
                                                        type="number"
                                                        value={widgetYAxisMax ?? ''}
                                                        onChange={(event) => setWidgetForm((prev) => ({
                                                            ...prev,
                                                            viz_config: {
                                                                ...(prev.viz_config || {}),
                                                                y_axis_max: event.target.value,
                                                            },
                                                        }))}
                                                        helperText="Vacío = automático"
                                                    />
                                                </Grid>
                                            </>
                                        )}

                                        {activeWidgetType === 'stat' && (
                                            <Grid item xs={12} md={6}>
                                                <Box
                                                    sx={{
                                                        border: `1px solid ${alpha('#64748B', 0.24)}`,
                                                        borderRadius: 1.5,
                                                        px: 1.25,
                                                        py: 0.75,
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent: 'center',
                                                    }}>
                                                    <FormControlLabel
                                                        sx={{ m: 0 }}
                                                        control={
                                                            <Switch
                                                                checked={parseBooleanLike(widgetForm?.viz_config?.show_kpi_background)}
                                                                onChange={(event) => setWidgetForm((prev) => ({
                                                                    ...prev,
                                                                    viz_config: {
                                                                        ...(prev.viz_config || {}),
                                                                        show_kpi_background: event.target.checked,
                                                                    },
                                                                }))}
                                                            />
                                                        }
                                                        label="Mostrar gráfico histórico de fondo"
                                                    />
                                                    <Typography variant="caption" color="text.secondary" sx={{ pl: 0.25 }}>
                                                        Usa la métrica principal del KPI con un color suave.
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Paper>
                            )}

                            {activeWidgetType === 'table' && (
                                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                        <Typography variant="subtitle2">Vista de tabla</Typography>
                                        <Button
                                            size="small"
                                            onClick={() => setWidgetForm((prev) => {
                                                const currentTableView = normalizeTableViewConfig(
                                                    prev?.viz_config?.table_view || {},
                                                    prev?.query_config?.y_metrics || []
                                                );
                                                const nextColumns = [
                                                    ...currentTableView.columns,
                                                    {
                                                        id: `col_${Date.now()}`,
                                                        key: tableColumnKeyOptions[0]?.key || 'bucket_time',
                                                        label: 'Nueva columna',
                                                        type: 'text',
                                                    },
                                                ];
                                                return {
                                                    ...prev,
                                                    viz_config: {
                                                        ...(prev.viz_config || {}),
                                                        table_view: normalizeTableViewConfig(
                                                            { ...currentTableView, columns: nextColumns },
                                                            prev?.query_config?.y_metrics || []
                                                        ),
                                                    },
                                                };
                                            })}
                                        >
                                            Agregar columna
                                        </Button>
                                    </Stack>

                                    <Grid container spacing={1.25} sx={{ mb: 1.25 }}>
                                        <Grid item xs={12} md={4}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Formato fecha/hora</InputLabel>
                                                <Select
                                                    label="Formato fecha/hora"
                                                    value={widgetTableView.datetime_format}
                                                    onChange={(event) => setWidgetForm((prev) => {
                                                        const currentTableView = normalizeTableViewConfig(
                                                            prev?.viz_config?.table_view || {},
                                                            prev?.query_config?.y_metrics || []
                                                        );
                                                        return {
                                                            ...prev,
                                                            viz_config: {
                                                                ...(prev.viz_config || {}),
                                                                table_view: normalizeTableViewConfig(
                                                                    { ...currentTableView, datetime_format: event.target.value },
                                                                    prev?.query_config?.y_metrics || []
                                                                ),
                                                            },
                                                        };
                                                    })}
                                                >
                                                    {TABLE_DATETIME_FORMATS.map((formatKey) => (
                                                        <MenuItem key={`table-datetime-format-${formatKey}`} value={formatKey}>
                                                            {`${TABLE_DATETIME_FORMAT_LABELS[formatKey]} (ej: ${formatTableDateTime(TABLE_DATETIME_FORMAT_SAMPLE, formatKey)})`}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>

                                    <Stack spacing={1}>
                                        {widgetTableView.columns.map((column, columnIndex) => (
                                            <Grid container spacing={1} alignItems="center" key={column.id || `table-col-${columnIndex}`}>
                                                <Grid item xs={12} md={4}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Campo</InputLabel>
                                                        <Select
                                                            label="Campo"
                                                            value={column.key}
                                                            onChange={(event) => setWidgetForm((prev) => {
                                                                const currentTableView = normalizeTableViewConfig(
                                                                    prev?.viz_config?.table_view || {},
                                                                    prev?.query_config?.y_metrics || []
                                                                );
                                                                const nextColumns = currentTableView.columns.map((currentColumn, currentIndex) => (
                                                                    currentIndex === columnIndex
                                                                        ? { ...currentColumn, key: event.target.value }
                                                                        : currentColumn
                                                                ));
                                                                return {
                                                                    ...prev,
                                                                    viz_config: {
                                                                        ...(prev.viz_config || {}),
                                                                        table_view: normalizeTableViewConfig(
                                                                            { ...currentTableView, columns: nextColumns },
                                                                            prev?.query_config?.y_metrics || []
                                                                        ),
                                                                    },
                                                                };
                                                            })}
                                                        >
                                                            {tableColumnKeyOptions.map((option) => (
                                                                <MenuItem key={`table-col-option-${option.key}`} value={option.key}>
                                                                    {option.label}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                <Grid item xs={12} md={4}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label="Título de columna"
                                                        value={column.label}
                                                        onChange={(event) => setWidgetForm((prev) => {
                                                            const currentTableView = normalizeTableViewConfig(
                                                                prev?.viz_config?.table_view || {},
                                                                prev?.query_config?.y_metrics || []
                                                            );
                                                            const nextColumns = currentTableView.columns.map((currentColumn, currentIndex) => (
                                                                currentIndex === columnIndex
                                                                    ? { ...currentColumn, label: event.target.value }
                                                                    : currentColumn
                                                            ));
                                                            return {
                                                                ...prev,
                                                                viz_config: {
                                                                    ...(prev.viz_config || {}),
                                                                    table_view: normalizeTableViewConfig(
                                                                        { ...currentTableView, columns: nextColumns },
                                                                        prev?.query_config?.y_metrics || []
                                                                    ),
                                                                },
                                                            };
                                                        })}
                                                    />
                                                </Grid>

                                                <Grid item xs={10} md={3}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Tipo</InputLabel>
                                                        <Select
                                                            label="Tipo"
                                                            value={column.type}
                                                            onChange={(event) => setWidgetForm((prev) => {
                                                                const currentTableView = normalizeTableViewConfig(
                                                                    prev?.viz_config?.table_view || {},
                                                                    prev?.query_config?.y_metrics || []
                                                                );
                                                                const nextColumns = currentTableView.columns.map((currentColumn, currentIndex) => (
                                                                    currentIndex === columnIndex
                                                                        ? { ...currentColumn, type: event.target.value }
                                                                        : currentColumn
                                                                ));
                                                                return {
                                                                    ...prev,
                                                                    viz_config: {
                                                                        ...(prev.viz_config || {}),
                                                                        table_view: normalizeTableViewConfig(
                                                                            { ...currentTableView, columns: nextColumns },
                                                                            prev?.query_config?.y_metrics || []
                                                                        ),
                                                                    },
                                                                };
                                                            })}
                                                        >
                                                            <MenuItem value="text">Texto</MenuItem>
                                                            <MenuItem value="number">Número</MenuItem>
                                                            <MenuItem value="datetime">Fecha/Hora</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                <Grid item xs={2} md={1}>
                                                    <IconButton
                                                        color="error"
                                                        disabled={widgetTableView.columns.length <= 1}
                                                        onClick={() => setWidgetForm((prev) => {
                                                            const currentTableView = normalizeTableViewConfig(
                                                                prev?.viz_config?.table_view || {},
                                                                prev?.query_config?.y_metrics || []
                                                            );
                                                            const nextColumns = currentTableView.columns.filter((_column, currentIndex) => currentIndex !== columnIndex);
                                                            return {
                                                                ...prev,
                                                                viz_config: {
                                                                    ...(prev.viz_config || {}),
                                                                    table_view: normalizeTableViewConfig(
                                                                        { ...currentTableView, columns: nextColumns },
                                                                        prev?.query_config?.y_metrics || []
                                                                    ),
                                                                },
                                                            };
                                                        })}
                                                    >
                                                        <RemoveCircleOutlineOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </Stack>
                                </Paper>
                            )}

                            {['timeseries', 'bar', 'stat', 'table'].includes(activeWidgetType) && (
                                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Límites de control
                                    </Typography>

                                    <Grid container spacing={1.25}>
                                        <Grid item xs={12} md={3}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Estado</InputLabel>
                                                <Select
                                                    label="Estado"
                                                    value={widgetControlLimits.enabled ? 'enabled' : 'disabled'}
                                                    onChange={(event) => setWidgetForm((prev) => ({
                                                        ...prev,
                                                        viz_config: {
                                                            ...(prev.viz_config || {}),
                                                            control_limits: {
                                                                ...(prev.viz_config?.control_limits || {}),
                                                                enabled: event.target.value === 'enabled',
                                                            },
                                                        },
                                                    }))}>
                                                    <MenuItem value="disabled">Deshabilitado</MenuItem>
                                                    <MenuItem value="enabled">Habilitado</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} md={3}>
                                            <FormControl fullWidth size="small" disabled={!widgetControlLimits.enabled}>
                                                <InputLabel>Regla</InputLabel>
                                                <Select
                                                    label="Regla"
                                                    value={widgetControlLimits.mode}
                                                    onChange={(event) => setWidgetForm((prev) => ({
                                                        ...prev,
                                                        viz_config: {
                                                            ...(prev.viz_config || {}),
                                                            control_limits: {
                                                                ...(prev.viz_config?.control_limits || {}),
                                                                mode: event.target.value,
                                                            },
                                                        },
                                                    }))}>
                                                    <MenuItem value="inside">Valores deben estar dentro</MenuItem>
                                                    <MenuItem value="outside">Valores deben estar fuera</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                disabled={!widgetControlLimits.enabled}
                                                label="Mínimo"
                                                type="number"
                                                value={widgetControlLimits.min ?? ''}
                                                onChange={(event) => setWidgetForm((prev) => ({
                                                    ...prev,
                                                    viz_config: {
                                                        ...(prev.viz_config || {}),
                                                        control_limits: {
                                                            ...(prev.viz_config?.control_limits || {}),
                                                            min: event.target.value,
                                                        },
                                                    },
                                                }))}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                disabled={!widgetControlLimits.enabled}
                                                label="Máximo"
                                                type="number"
                                                value={widgetControlLimits.max ?? ''}
                                                onChange={(event) => setWidgetForm((prev) => ({
                                                    ...prev,
                                                    viz_config: {
                                                        ...(prev.viz_config || {}),
                                                        control_limits: {
                                                            ...(prev.viz_config?.control_limits || {}),
                                                            max: event.target.value,
                                                        },
                                                    },
                                                }))}
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            )}

                            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.2}>
                                    <Typography variant="subtitle2">Preview</Typography>
                                    <Button size="small" onClick={handlePreviewWidget} disabled={previewLoading}>
                                        {previewLoading ? 'Consultando...' : 'Previsualizar'}
                                    </Button>
                                </Stack>

                                {previewLoading && (
                                    <Box sx={{ textAlign: 'center', py: 2 }}>
                                        <CircularProgress size={22} />
                                    </Box>
                                )}

                                {!previewLoading && previewResult?.ok === false && (
                                    <Alert severity="error">No fue posible generar el preview.</Alert>
                                )}

                                {!previewLoading && previewResult?.ok && (
                                    <WidgetResultView
                                        widget={widgetForm}
                                        result={previewResult.data}
                                        loading={false}
                                        error={false}
                                        mapApiLoaded={mapApiLoaded}
                                        mapEnabled={mapEnabled}
                                        dashboardDateRangeState={runtimeDateRange}
                                    />
                                )}
                            </Paper>
                        </Stack>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={closeWidgetDialog}>Cancelar</Button>
                    <Button variant="contained" onClick={handleWidgetSave}>Guardar widget</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DashboardsIndex;
